import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';
import Modal from './Modal';
import ConfirmModal from './ConfirmModal';
import InvoiceDetailModal from './InvoiceDetailModal';
import Toast from './Toast';
import { QuickAddVendorModal } from './QuickAddModal';

// Canonical buckets — Phase 11.1 collapsed normalization to three. Mirrors
// the canonical_unit values returned by the SQL `normalize_uom()` function.
// "+ Add new..." sentinel handles exotic per-line cases.
const BASE_UNIT_OPTIONS = ['fl_oz', 'oz', 'each'];
const BASE_UNIT_NEW_SENTINEL = '__add_new__';

/**
 * ClaudeOCRReview - Review and edit invoice data extracted by COGSLOG.
 *
 * Saves invoices directly via API (no sessionStorage/navigate). Shows editable form
 * with PDF preview, inline category creation, and SKU catalog update confirmation.
 *
 * @param {Object} props
 * @param {Object} props.extractedData - Data from COGSLOG extraction
 * @param {string} props.filepath - Server path to the PDF
 * @param {string} [props.previewImage] - Base64 preview image
 * @param {number|null} [props.vendorId] - Detected vendor ID from upload step
 * @param {string} [props.vendorName] - Detected vendor name from upload step
 * @param {string|number} [props.activeBusiness] - Currently selected business ID
 * @param {Function} [props.onReExtract] - Callback to retry extraction
 * @param {Function} [props.onBack] - Callback to go back to upload
 * @param {number} [props.queueItemId] - Queue item ID (when rendered from batch queue)
 * @param {Function} [props.onDeleteFromQueue] - Callback to delete item from queue (batch mode)
 * @param {Function} [props.onReviewNext] - Callback to advance to next item (batch mode only)
 * @param {Function} [props.onCloseReview] - Callback to return to queue (batch mode only)
 * @component
 */
function ClaudeOCRReview({ extractedData, filepath, previewImage, previewPages, previewPageUrls, vendorId, vendorName, activeBusiness, onReExtract, reExtracting, onBack, queueItemId, queueFilename, onDeleteFromQueue, onReviewNext, onCloseReview, onDirtyChange, imageQualityWarning }) {
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [newCategoryApprovals, setNewCategoryApprovals] = useState({});
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [duplicateError, setDuplicateError] = useState(false);

  // Append-to-existing invoice state (multi-page support)
  const [existingInvoiceId, setExistingInvoiceId] = useState(null);
  // GH #155 (Ruth UAT 2026-05-28): when the duplicate lives in a different
  // business than the one the user is currently saving to, "View Existing"
  // fetched an invoice the UI couldn't render properly (or 404'd). Track
  // the scope so we can surface a clearer message and skip the broken
  // fetch path. null = unknown (legacy), true = same business as current
  // save, false = different business in the same org.
  const [duplicateInCurrentBusiness, setDuplicateInCurrentBusiness] = useState(null);
  const [existingInvoiceBusinessId, setExistingInvoiceBusinessId] = useState(null);
  const [showAppendComparison, setShowAppendComparison] = useState(false);
  const [existingInvoice, setExistingInvoice] = useState(null);
  const [appending, setAppending] = useState(false);
  const [showExistingInvoice, setShowExistingInvoice] = useState(false);

  // Mobile-only tab state. The Review page on phone is too long to scroll
  // through linearly (PDF preview + invoice details + business/vendor +
  // line items + misc charges + sticky footer is ~3000-4000px tall on a
  // 390px viewport). At <=768px we split the content into three tabs:
  // Details (default — invoice header, business, vendor), Items (line
  // items + misc charges), and PDF (source preview). Desktop is unaffected
  // — the CSS shows all tab panes side-by-side as before. The action
  // footer is always visible regardless of active tab.
  const [activeMobileTab, setActiveMobileTab] = useState('details');

  // Direct save state
  const [businesses, setBusinesses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(activeBusiness || '');
  const [selectedVendorId, setSelectedVendorId] = useState(vendorId ? String(vendorId) : '');
  const [changingVendor, setChangingVendor] = useState(!vendorId);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [newSkusCount, setNewSkusCount] = useState(0);
  const [packSizeWarnings, setPackSizeWarnings] = useState([]);
  // GH #153 (Mark UAT 2026-05-27): inline UOM-fix form state for the
  // pack-size warning card. Keyed by the warning's SKU so each row owns
  // its own draft inputs + saving flag without polluting other rows.
  const [packFixDrafts, setPackFixDrafts] = useState({});
  const [packFixSaving, setPackFixSaving] = useState({});
  // Per-row "+ Add new..." flag for the base_unit override dropdown. Map of
  // line-item index → boolean. Persisted only in component state — we never
  // round-trip this flag to the backend, the saved value is just the string.
  const [baseUnitCustomByRow, setBaseUnitCustomByRow] = useState({});

  // Inline category creation
  const [addingCategoryForRow, setAddingCategoryForRow] = useState(null);
  const [newCatLevel1, setNewCatLevel1] = useState('');
  const [newCatLevel2, setNewCatLevel2] = useState('');
  const [newCatLevel3, setNewCatLevel3] = useState('');
  // Per-level "+ Add new..." escape hatch flags. When true, the level renders
  // a text input below the select so the user can type a brand-new value.
  // Cleared whenever a parent level changes (cascade reset).
  const [newCatL1Custom, setNewCatL1Custom] = useState(false);
  const [newCatL2Custom, setNewCatL2Custom] = useState(false);
  const [newCatL3Custom, setNewCatL3Custom] = useState(false);

  // SKU catalog update confirmation
  const [skuUpdates, setSkuUpdates] = useState([]);
  const [showSkuConfirm, setShowSkuConfirm] = useState(false);
  const [updatingSkus, setUpdatingSkus] = useState(false);

  // Vendor settings
  const [fuelSurchargeInSubtotal, setFuelSurchargeInSubtotal] = useState(false);

  // Toast notification
  const [toast, setToast] = useState(null);

  // Confirmation modal
  const [confirmModal, setConfirmModal] = useState(null);

  // Dirty state tracking for unsaved changes warning
  const [isDirty, setIsDirty] = useState(false);

  // Confidence notes expand/collapse
  const [showConfidenceDetails, setShowConfidenceDetails] = useState(false);

  // Line item action menu and modal editing
  const [actionMenuIdx, setActionMenuIdx] = useState(null);
  const [editingRowIdx, setEditingRowIdx] = useState(null);
  const [showAddLineModal, setShowAddLineModal] = useState(false);
  const [newLineForm, setNewLineForm] = useState({ sku: '', description: '', quantity: 1, unit_of_measure: '', unit_price: 0 });
  const [showAddMiscModal, setShowAddMiscModal] = useState(false);
  const [newMiscForm, setNewMiscForm] = useState({ description: '', amount: 0 });
  const actionMenuRef = useRef(null);

  // Invoice details edit mode
  const [editingDetails, setEditingDetails] = useState(false);

  // Rotation state (Mark UAT 2026-05-26). Two phases tracked separately:
  //   `rotating` — fast S3 round-trip + preview regen (1-3 seconds)
  //   `rotatingReExtracting` — chained Claude Vision call (10-60 seconds)
  // The second phase reuses the same full-viewport overlay as the
  // `reExtracting` prop so the user knows extraction is in flight; the
  // tiny inline status was too easy to miss.
  const [rotating, setRotating] = useState(false);
  const [rotatingReExtracting, setRotatingReExtracting] = useState(false);
  // Mark UAT 2026-05-27 (GH #151): clicking "Review Next Invoice" triggers
  // 3 parallel network calls in the parent (getOCRQueue, getCategories,
  // getQueueItemPreview) — the preview alone can take 5-10s for large PDFs.
  // Without a busy state on the button, the user thinks the click did
  // nothing and starts clicking other things. This state disables the
  // button + relabels it for the duration.
  const [loadingNext, setLoadingNext] = useState(false);
  const [localPreviewImage, setLocalPreviewImage] = useState(null);

  // Zoom lightbox state
  const [zoomOpen, setZoomOpen] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const invoiceNumRef = useRef(null);
  const zoomModalRef = useRef(null);
  const previewImageRef = useRef(null);
  const touchStartRef = useRef(null);

  // Initialize state from extractedData — reset all transient UI state so stale
  // errors/success from a previous invoice don't bleed into the next review.
  useEffect(() => {
    if (extractedData) {
      categoriesResolved.current = false;
      // Stamp actual_total from OCR line_total on each line item so we can
      // detect discrepancies when qty * unit_price doesn't match the invoice.
      const stampedItems = (extractedData.line_items || []).map(item => ({
        ...item,
        actual_total: item.line_total != null ? parseFloat(item.line_total) || 0 : undefined,
      }));
      setData({ ...extractedData, line_items: stampedItems });
      const approvals = {};
      (extractedData.new_categories || []).forEach((cat, idx) => {
        approvals[idx] = true;
      });
      setNewCategoryApprovals(approvals);
      // Auto-detect fuel surcharge in subtotal pattern using final line item data.
      // If line items sum already matches the stated subtotal (within tolerance),
      // and fuel > 0, then fuel must be included in the subtotal (e.g. as a line item).
      const lineSum = stampedItems.reduce((s, i) => s + (parseFloat(i.line_total) || 0), 0);
      const fuel = parseFloat(extractedData.fuel_surcharge) || 0;
      const statedSubtotal = parseFloat(extractedData.subtotal) || 0;
      if (fuel > 0 && statedSubtotal > 0) {
        const diffWithout = Math.abs(lineSum - statedSubtotal);
        const diffWithFuel = Math.abs((lineSum + fuel) - statedSubtotal);
        // If line items sum matches subtotal WITHOUT adding fuel → fuel is separate (not in subtotal)
        // If line items sum + fuel matches subtotal → fuel IS included in the subtotal
        if (diffWithout < 1.0) {
          // Line items already match subtotal — fuel is a separate charge
          setFuelSurchargeInSubtotal(false);
          autoDetectedFuel.current = true;
        } else if (diffWithFuel < 1.0) {
          // Line items + fuel matches subtotal — fuel is included in subtotal
          setFuelSurchargeInSubtotal(true);
          autoDetectedFuel.current = true;
        } else {
          // Neither matches — fall back to backend detection
          autoDetectedFuel.current = false;
        }
      } else if (extractedData.fuel_surcharge_in_subtotal !== undefined) {
        setFuelSurchargeInSubtotal(extractedData.fuel_surcharge_in_subtotal);
        autoDetectedFuel.current = true;
      }
      setSaveSuccess(false);
      setValidationErrors([]);
      setDuplicateError(false);
      setIsDirty(false);
      setToast(null);
    }
  }, [extractedData]);

  // Notify parent of dirty state changes (C6: unsaved changes check)
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  // Load businesses, vendors, and categories with AbortController cleanup
  useEffect(() => {
    const controller = new AbortController();
    const loadData = async () => {
      try {
        const [bizRes, vendorRes, catRes] = await Promise.all([
          api.getBusinesses(),
          api.getVendors(),
          api.getCategories(),
        ]);
        if (controller.signal.aborted) return;
        setBusinesses(bizRes.data);
        // Auto-select business when only one exists and none is pre-selected
        if (!activeBusiness && bizRes.data.length === 1) {
          setSelectedBusinessId(String(bizRes.data[0].id));
        }
        setVendors(vendorRes.data.filter(v => v.status === 'active'));
        setCategories(catRes.data.filter(c => c.status === 'active'));
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error('Failed to load data:', err);
      }
    };
    loadData();
    return () => controller.abort();
  }, []);

  // Pre-populate vendor settings when vendor changes — but only if
  // the auto-detection from extractedData didn't already set it.
  // The vendor setting is a default; the invoice-level detection takes priority.
  const autoDetectedFuel = useRef(false);
  useEffect(() => {
    if (selectedVendorId && vendors.length > 0 && !autoDetectedFuel.current) {
      const vendor = vendors.find(v => v.id === parseInt(selectedVendorId));
      if (vendor) {
        setFuelSurchargeInSubtotal(!!vendor.fuel_surcharge_in_subtotal);
      }
    }
  }, [selectedVendorId, vendors]);

  /**
   * Derive whether the selected vendor uses SKU numbers.
   * @returns {boolean} True if the vendor uses SKUs (default true)
   */
  const vendorUsesSku = (() => {
    if (!selectedVendorId || vendors.length === 0) return true;
    const vendor = vendors.find(v => v.id === parseInt(selectedVendorId));
    return vendor ? vendor.uses_sku !== 0 : true;
  })();

  /**
   * Find a category by level1/level2/level3 name match.
   * @param {Object} sc - Suggested category with level1/level2/level3 fields
   * @param {Array} cats - Available categories from the database
   * @returns {Object|null} Matching category or null
   */
  const findCategoryByName = useCallback((sc, cats) => {
    if (!sc?.level1 || !cats?.length) return null;
    return cats.find(c =>
      c.level1 === sc.level1 &&
      (c.level2 || '') === (sc.level2 || '') &&
      (c.level3 || '') === (sc.level3 || '')
    ) || null;
  }, []);

  const categoriesResolved = useRef(false);

  // Auto-resolve category IDs by name when COGSLOG returns null category_id
  useEffect(() => {
    if (!data || categories.length === 0 || categoriesResolved.current) return;

    let changed = false;
    const updatedItems = data.line_items.map(item => {
      const sc = item.suggested_category;
      if (sc && !sc.category_id && !sc.is_new && sc.level1) {
        const match = findCategoryByName(sc, categories);
        if (match) {
          changed = true;
          return {
            ...item,
            suggested_category: { ...sc, category_id: match.id },
            _autoMatched: true,
          };
        }
      }
      return item;
    });

    if (changed) {
      categoriesResolved.current = true;
      setData(prev => ({ ...prev, line_items: updatedItems }));
      const matchCount = updatedItems.filter(i => i._autoMatched).length;
      setToast({ message: `${matchCount} ${matchCount === 1 ? 'category' : 'categories'} auto-matched from suggestions`, type: 'info' });
    }
  }, [categories, data, findCategoryByName]);

  /**
   * Update a header field value.
   * @param {string} field - Field name
   * @param {*} value - New value
   */
  const updateField = useCallback((field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  }, []);

  /**
   * Update a line item field.
   * @param {number} index - Line item index
   * @param {string} field - Field name
   * @param {*} value - New value
   */
  const updateLineItem = useCallback((index, field, value) => {
    setData(prev => {
      const items = [...prev.line_items];
      items[index] = { ...items[index], [field]: value };
      if (field === 'quantity' || field === 'unit_price') {
        const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(items[index].quantity) || 0;
        const price = field === 'unit_price' ? parseFloat(value) || 0 : parseFloat(items[index].unit_price) || 0;
        items[index].line_total = Math.round(qty * price * 100) / 100;
        // actual_total is intentionally preserved — it holds the OCR-extracted
        // invoice amount which may differ from qty * unit_price.
      }
      return { ...prev, line_items: items };
    });
    setIsDirty(true);
  }, []);

  // Phase 11 Wave 7 (NORM-05): when the Edit Line Item modal opens and the
  // line item has a SKU, look it up in the vendor's catalog and stash the
  // matched SKU's base_unit + base_units_per_pack on the line item under
  // matched_sku_* keys. The override inputs read these for their placeholder
  // text — operator sees "Defaults to 'egg' from matched SKU" and only types
  // a value to override the default. We don't auto-fill because that would
  // promote a placeholder hint into a real override, defeating the purpose.
  useEffect(() => {
    if (editingRowIdx == null) return;
    const item = data?.line_items?.[editingRowIdx];
    if (!item) return;
    if (!item.sku || !selectedVendorId) return;
    // Skip if we've already hydrated for this line item (matched_sku_* present).
    if ('matched_sku_base_unit' in item || 'matched_sku_base_units_per_pack' in item) return;

    let cancelled = false;
    api.getSKUCatalog({ vendor_id: parseInt(selectedVendorId), sku: item.sku })
      .then(res => {
        if (cancelled) return;
        const matched = Array.isArray(res?.data) && res.data.length > 0 ? res.data[0] : null;
        // Always tag the row even when there is no match (null), so this
        // effect doesn't refire on every render.
        setData(prev => {
          const items = [...(prev?.line_items || [])];
          if (!items[editingRowIdx]) return prev;
          items[editingRowIdx] = {
            ...items[editingRowIdx],
            matched_sku_base_unit: matched?.base_unit ?? null,
            matched_sku_base_units_per_pack: matched?.base_units_per_pack ?? null,
          };
          return { ...prev, line_items: items };
        });
      })
      .catch(() => {
        // SKU lookup failure is non-fatal — operator can still fill manually.
      });

    return () => { cancelled = true; };
  // We intentionally watch editingRowIdx + selectedVendorId only; data churn
  // would cause re-fetch storms. The 'matched_sku_*' tag short-circuits.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingRowIdx, selectedVendorId]);

  /**
   * Remove a line item by index.
   * @param {number} index - Index to remove
   */
  /**
   * Remove a line item by index after user confirmation.
   * @param {number} index - Index to remove
   */
  const removeLineItem = useCallback((index) => {
    setConfirmModal({
      title: 'Remove Line Item',
      message: 'Remove this line item?',
      confirmLabel: 'Remove',
      variant: 'danger',
      onConfirm: () => {
        setConfirmModal(null);
        setData(prev => ({
          ...prev,
          line_items: prev.line_items.filter((_, i) => i !== index)
        }));
        setIsDirty(true);
      },
    });
  }, []);

  /**
   * Add a new empty line item.
   */
  const addLineItemFromForm = useCallback(() => {
    const total = (parseFloat(newLineForm.quantity) || 0) * (parseFloat(newLineForm.unit_price) || 0);
    setData(prev => ({
      ...prev,
      line_items: [...prev.line_items, {
        ...newLineForm,
        line_total: Math.round(total * 100) / 100,
        suggested_category: null,
      }]
    }));
    setNewLineForm({ sku: '', description: '', quantity: 1, unit_of_measure: '', unit_price: 0 });
    setShowAddLineModal(false);
    setIsDirty(true);
  }, [newLineForm]);

  /**
   * Update a misc charge field.
   * @param {number} index - Charge index
   * @param {string} field - Field name
   * @param {*} value - New value
   */
  const updateMiscCharge = useCallback((index, field, value) => {
    setData(prev => {
      const charges = [...prev.misc_charges];
      charges[index] = { ...charges[index], [field]: value };
      return { ...prev, misc_charges: charges };
    });
    setIsDirty(true);
  }, []);

  /**
   * Remove a misc charge.
   * @param {number} index - Index to remove
   */
  /**
   * Remove a misc charge after user confirmation.
   * @param {number} index - Index to remove
   */
  const removeMiscCharge = useCallback((index) => {
    setConfirmModal({
      title: 'Remove Charge',
      message: 'Remove this charge?',
      confirmLabel: 'Remove',
      variant: 'danger',
      onConfirm: () => {
        setConfirmModal(null);
        setData(prev => ({
          ...prev,
          misc_charges: prev.misc_charges.filter((_, i) => i !== index)
        }));
        setIsDirty(true);
      },
    });
  }, []);

  /**
   * Add a new empty misc charge.
   */
  const addMiscCharge = useCallback(() => {
    setData(prev => ({
      ...prev,
      misc_charges: [...(prev.misc_charges || []), { description: '', amount: 0 }]
    }));
  }, []);

  /**
   * Handle mouse wheel zoom inside the zoom modal.
   * Uses ref-based event listener with { passive: false } to allow preventDefault().
   * @param {WheelEvent} e - Native wheel event
   */
  const handleZoomWheel = useCallback((e) => {
    e.preventDefault();
    setZoomLevel(prev => {
      const delta = e.deltaY > 0 ? -0.25 : 0.25;
      const newZoom = Math.min(Math.max(prev + delta, 1), 4);
      if (newZoom <= 1) setPanPosition({ x: 0, y: 0 });
      return newZoom;
    });
  }, []);

  /**
   * Start drag-to-pan when mouse is pressed inside zoom modal.
   * @param {React.MouseEvent} e - Mouse event
   */
  const handlePanStart = useCallback((e) => {
    if (zoomLevel <= 1) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
  }, [zoomLevel, panPosition]);

  /**
   * Update pan position during drag.
   * @param {React.MouseEvent} e - Mouse event
   */
  const handlePanMove = useCallback((e) => {
    if (!isDragging) return;
    setPanPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  }, [isDragging, dragStart]);

  /**
   * End drag-to-pan on mouse release or leave.
   */
  const handlePanEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  /**
   * Calculate distance between two touch points.
   * @param {Touch} t1 - First touch point
   * @param {Touch} t2 - Second touch point
   * @returns {number} Distance in pixels
   */
  const getTouchDistance = useCallback((t1, t2) => {
    return Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
  }, []);

  /**
   * Handle touch start for pan (single finger) and pinch-to-zoom (two fingers).
   * @param {React.TouchEvent} e - Touch event
   */
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      // Pinch start: record initial distance
      const dist = getTouchDistance(e.touches[0], e.touches[1]);
      touchStartRef.current = { type: 'pinch', initialDistance: dist, initialZoom: zoomLevel };
    } else if (e.touches.length === 1 && zoomLevel > 1) {
      // Single finger pan
      touchStartRef.current = { type: 'pan', x: e.touches[0].clientX - panPosition.x, y: e.touches[0].clientY - panPosition.y };
      setIsDragging(true);
    }
  }, [zoomLevel, panPosition, getTouchDistance]);

  /**
   * Handle touch move for pan and pinch-to-zoom.
   * @param {React.TouchEvent} e - Touch event
   */
  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    if (!touchStartRef.current) return;

    if (touchStartRef.current.type === 'pinch' && e.touches.length === 2) {
      const dist = getTouchDistance(e.touches[0], e.touches[1]);
      const ratio = dist / touchStartRef.current.initialDistance;
      const newZoom = Math.min(Math.max(touchStartRef.current.initialZoom * ratio, 1), 4);
      setZoomLevel(newZoom);
      if (newZoom <= 1) setPanPosition({ x: 0, y: 0 });
    } else if (touchStartRef.current.type === 'pan' && e.touches.length === 1) {
      setPanPosition({
        x: e.touches[0].clientX - touchStartRef.current.x,
        y: e.touches[0].clientY - touchStartRef.current.y,
      });
    }
  }, [getTouchDistance]);

  /**
   * Handle touch end: clear touch state.
   */
  const handleTouchEnd = useCallback(() => {
    touchStartRef.current = null;
    setIsDragging(false);
  }, []);

  /**
   * Handle double-click to toggle zoom: 1x -> 2x centered on click, >1x -> 1x reset.
   * @param {React.MouseEvent} e - Mouse event
   */
  const handleDoubleClick = useCallback((e) => {
    if (zoomLevel > 1) {
      // Reset to 1x
      setZoomLevel(1);
      setPanPosition({ x: 0, y: 0 });
    } else {
      // Zoom to 2x centered on click position
      const rect = e.currentTarget.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;
      setPanPosition({ x: (centerX - clickX), y: (centerY - clickY) });
      setZoomLevel(2);
    }
  }, [zoomLevel]);

  /**
   * Close the zoom modal and reset zoom/pan state.
   * Returns focus to the preview image for keyboard accessibility.
   */
  const closeZoom = useCallback(() => {
    setZoomOpen(false);
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
    if (previewImageRef.current) previewImageRef.current.focus();
  }, []);

  // CRITICAL: Use ref-based wheel event listener with { passive: false }.
  // React's synthetic onWheel registers as passive in React 17+, which means
  // e.preventDefault() would be silently ignored and the page would scroll
  // behind the modal while the user tries to zoom.
  useEffect(() => {
    if (!zoomOpen || !zoomModalRef.current) return;
    const el = zoomModalRef.current;
    el.addEventListener('wheel', handleZoomWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleZoomWheel);
  }, [zoomOpen, handleZoomWheel]);

  // Body scroll lock: prevent background page from scrolling while modal is open
  useEffect(() => {
    if (!zoomOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [zoomOpen]);

  // Keyboard: Escape to close, +/-/0 for zoom, focus trap within modal
  useEffect(() => {
    if (!zoomOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeZoom();
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        setZoomLevel(prev => Math.min(prev + 0.5, 4));
      }
      if (e.key === '-') {
        e.preventDefault();
        setZoomLevel(prev => {
          const newZoom = Math.max(prev - 0.5, 1);
          if (newZoom <= 1) setPanPosition({ x: 0, y: 0 });
          return newZoom;
        });
      }
      if (e.key === '0') {
        e.preventDefault();
        setZoomLevel(1);
        setPanPosition({ x: 0, y: 0 });
      }
      if (e.key === 'Tab' && zoomModalRef.current) {
        const focusable = zoomModalRef.current.querySelectorAll('button');
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    if (zoomModalRef.current) {
      const firstBtn = zoomModalRef.current.querySelector('button');
      if (firstBtn) firstBtn.focus();
    }
    return () => window.removeEventListener('keydown', handleKey);
  }, [zoomOpen, closeZoom]);

  // Close action menu on click outside
  useEffect(() => {
    if (actionMenuIdx === null) return;
    const handleClick = (e) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        setActionMenuIdx(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [actionMenuIdx]);

  /**
   * Create a new category inline and assign it to the specified row.
   * @param {number} rowIndex - Line item index to assign the new category to
   */
  const handleCreateInlineCategory = useCallback(async (rowIndex) => {
    if (!newCatLevel1.trim()) return;

    try {
      const response = await api.createCategory({
        level1: newCatLevel1.trim(),
        level2: newCatLevel2.trim() || null,
        level3: newCatLevel3.trim() || null,
      });
      const newCatId = response.data.id;

      // Refresh categories
      const catRes = await api.getCategories();
      setCategories(catRes.data.filter(c => c.status === 'active'));

      // Assign to row
      setData(prev => {
        const items = [...prev.line_items];
        items[rowIndex] = {
          ...items[rowIndex],
          suggested_category: { category_id: newCatId, is_new: false },
        };
        return { ...prev, line_items: items };
      });

      // Reset inline form
      setAddingCategoryForRow(null);
      setNewCatLevel1('');
      setNewCatLevel2('');
      setNewCatLevel3('');
      setNewCatL1Custom(false);
      setNewCatL2Custom(false);
      setNewCatL3Custom(false);
    } catch (err) {
      console.error('Failed to create category:', err);
    }
  }, [newCatLevel1, newCatLevel2, newCatLevel3]);

  /**
   * Validate data before saving.
   * @returns {string[]} Array of validation error messages
   */
  const validate = useCallback(() => {
    const errors = [];
    const warnings = [];
    if (!data) return { errors: ['No data to validate'], warnings: [] };

    if (!selectedBusinessId) errors.push('Please select a business');
    if (!selectedVendorId) errors.push('Please select a vendor');
    if (!data.invoice_date) errors.push('Invoice date is required');
    if (!data.line_items || data.line_items.length === 0) errors.push('At least one line item is required');

    const lineTotal = data.line_items.reduce((sum, item) => {
      // Prefer actual_total (OCR-extracted) over calculated line_total
      const amount = item.actual_total != null ? item.actual_total : (parseFloat(item.line_total) || 0);
      return sum + amount;
    }, 0);
    const miscTotal = (data.misc_charges || []).reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
    const fuelSurcharge = parseFloat(data.fuel_surcharge) || 0;
    // When fuel surcharge is in subtotal, the stated subtotal includes it
    const expectedSubtotal = fuelSurchargeInSubtotal
      ? lineTotal + miscTotal + fuelSurcharge
      : lineTotal + miscTotal;
    const stated = parseFloat(data.subtotal) || 0;
    if (stated > 0 && Math.abs(expectedSubtotal - stated) > 0.02) {
      warnings.push(`Subtotal mismatch: items total $${expectedSubtotal.toFixed(2)} but subtotal says $${stated.toFixed(2)}`);
    }

    const tax = parseFloat(data.tax) || 0;
    // When fuel surcharge is in subtotal, don't add it again to the total
    const expectedTotal = fuelSurchargeInSubtotal
      ? stated + tax
      : stated + tax + fuelSurcharge;
    const statedTotal = parseFloat(data.total_amount) || 0;
    if (statedTotal > 0 && stated > 0 && Math.abs(expectedTotal - statedTotal) > 0.02) {
      warnings.push(`Total mismatch: subtotal + tax + fuel surcharge = $${expectedTotal.toFixed(2)} but total says $${statedTotal.toFixed(2)}`);
    }

    return { errors, warnings };
  }, [data, selectedBusinessId, selectedVendorId, fuelSurchargeInSubtotal]);

  /**
   * Resolve the category ID for a line item, handling new category creation.
   * @param {Object} item - Line item with suggested_category
   * @param {Object} createdCategoryMap - Map of new category keys to created IDs
   * @param {Array} newCats - Array of new categories from extraction
   * @returns {number|null} Resolved category ID
   */
  const resolveCategoryId = useCallback((item, createdCategoryMap, newCats) => {
    let categoryId = item.suggested_category?.category_id;
    if (item.suggested_category?.is_new) {
      const cat = item.suggested_category;
      const key = `${cat.level1}>${cat.level2}>${cat.level3}`;
      if (createdCategoryMap[key]) {
        categoryId = createdCategoryMap[key];
      } else if (!newCategoryApprovals[
        newCats.findIndex(c => c.level1 === cat.level1 && c.level2 === cat.level2 && c.level3 === cat.level3)
      ]) {
        categoryId = null;
      }
    }

    // Fallback: match by name if category_id is still null
    if (!categoryId && item.suggested_category && !item.suggested_category.is_new) {
      const match = findCategoryByName(item.suggested_category, categories);
      if (match) categoryId = match.id;
    }

    return categoryId || null;
  }, [newCategoryApprovals, categories, findCategoryByName]);

  /**
   * Check for SKU catalog entries that need category updates after saving.
   * @param {Array} lineItems - Saved line items with resolved category IDs
   */
  const checkSkuCatalogUpdates = useCallback(async (lineItems) => {
    const pendingUpdates = [];

    for (const item of lineItems) {
      if (!item.sku || !item.category_id) continue;

      try {
        const res = await api.getSKUCatalog({ vendor_id: parseInt(selectedVendorId), sku: item.sku });
        const skuEntries = res.data;
        if (!Array.isArray(skuEntries) || skuEntries.length === 0) continue;

        const existing = skuEntries[0];
        if (existing.category_id && existing.category_id !== item.category_id) {
          const oldCat = categories.find(c => c.id === existing.category_id);
          const newCat = categories.find(c => c.id === item.category_id);
          pendingUpdates.push({
            skuCatalogId: existing.id,
            sku: item.sku,
            oldCategoryLabel: oldCat ? [oldCat.level1, oldCat.level2, oldCat.level3].filter(Boolean).join(' > ') : '(none)',
            newCategoryLabel: newCat ? [newCat.level1, newCat.level2, newCat.level3].filter(Boolean).join(' > ') : '(unknown)',
            newCategoryId: item.category_id,
            existingData: existing,
          });
        }
      } catch {
        // SKU catalog lookup failed — skip
      }
    }

    if (pendingUpdates.length > 0) {
      setSkuUpdates(pendingUpdates);
      setShowSkuConfirm(true);
    }
  }, [selectedVendorId, categories]);

  /**
   * Apply all pending SKU catalog updates.
   */
  const handleApplySkuUpdates = useCallback(async () => {
    setUpdatingSkus(true);
    try {
      for (const update of skuUpdates) {
        await api.updateSKU(update.skuCatalogId, {
          ...update.existingData,
          category_id: update.newCategoryId,
        });
      }
    } catch (err) {
      console.error('Failed to update SKU catalog:', err);
    }
    setUpdatingSkus(false);
    setShowSkuConfirm(false);
    setSkuUpdates([]);
  }, [skuUpdates]);

  /**
   * Rotate the queue item's PDF 90° in the given direction, then chain a
   * re-extraction so the review form reflects Claude's read of the
   * correctly-oriented page.
   *
   * Mark UAT 2026-05-26 — Ruth's invoice came in rotated 90°. EXIF auto-
   * rotate at upload handles most iPhone photos automatically; this lets
   * users correct the edge cases (sideways scans, broken EXIF) without
   * re-uploading.
   *
   * @param {'left'|'right'} direction
   */
  const handleRotate = useCallback(async (direction) => {
    if (!queueItemId || rotating) return;
    setRotating(true);
    setToast(null);
    try {
      // Step 1: rotate the file in storage and get the new preview.
      const rotateResp = await api.rotateQueueItem(queueItemId, direction);
      const newPreview = rotateResp && rotateResp.data && rotateResp.data.preview_image;
      if (newPreview) {
        setLocalPreviewImage(newPreview);
      }

      // Step 2: re-run vision extraction against the rotated file.
      // Switch to the full-viewport "Re-extracting…" overlay so the user
      // knows the slow part is happening (Mark UAT 2026-05-26).
      setRotating(false);
      setRotatingReExtracting(true);
      const visionResp = await api.processQueueItemWithVision(queueItemId, {});
      const newExtracted = visionResp && visionResp.data && visionResp.data.extracted_data;
      if (newExtracted) {
        // Re-seed the form state from the fresh extraction.
        setData((prev) => ({ ...prev, ...newExtracted, line_items: newExtracted.line_items || prev.line_items }));
      }
      setToast({ message: 'Rotated and re-extracted.', type: 'success' });
    } catch (err) {
      // Tailor the error message to which phase failed so the user knows
      // whether to retry rotate or just retry the re-extract via the queue.
      const status = err && err.response && err.response.status;
      const serverErr = err && err.response && err.response.data && err.response.data.error;
      let message;
      if (status === 504) {
        message = (
          'The image was rotated, but Claude is taking longer than usual to ' +
          're-extract. Your extraction may still complete — check the queue ' +
          'in a moment.'
        );
      } else if (serverErr) {
        message = serverErr;
      } else {
        message = 'Rotation failed. Please try again.';
      }
      setToast({ message, type: 'error' });
    } finally {
      setRotating(false);
      setRotatingReExtracting(false);
    }
    // Intentionally omit `rotating` from deps so an in-flight handler isn't
    // replaced with a stale closure when setRotating(true) triggers a
    // re-render. The early-return + finally still gate concurrent clicks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueItemId]);

  /**
   * Save invoice directly via API.
   */
  const handleSaveInvoice = useCallback(async () => {
    const { errors, warnings } = validate();
    if (errors.length > 0) {
      setValidationErrors(errors);
      setToast({ message: errors[0], type: 'error' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    // Show warnings but don't block save
    if (warnings.length > 0) {
      setValidationErrors(warnings);
      setToast({ message: warnings[0], type: 'warning' });
    }

    setSaving(true);
    setDuplicateError(false);

    try {
      // Create approved new categories
      const newCats = data.new_categories || [];
      const createdCategoryMap = {};

      for (let i = 0; i < newCats.length; i++) {
        if (newCategoryApprovals[i]) {
          try {
            const response = await api.createCategory(newCats[i]);
            createdCategoryMap[`${newCats[i].level1}>${newCats[i].level2}>${newCats[i].level3}`] = response.data.id;
          } catch {
            // Category may already exist
          }
        }
      }

      // Build misc_charges (fuel surcharge is now a dedicated invoice field)
      const miscCharges = (data.misc_charges || []).map(c => ({
        description: c.description,
        amount: parseFloat(c.amount) || 0,
      }));

      // Map line items for invoice creation
      const lineItems = data.line_items.map(item => {
        const categoryId = resolveCategoryId(item, createdCategoryMap, newCats);
        // Use actual_total (OCR-extracted) when available; fall back to line_total or qty * price
        const totalPrice = item.actual_total != null
          ? item.actual_total
          : (parseFloat(item.line_total) || (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0));
        // Phase 11 Wave 7 (NORM-05): pass per-line-item normalization
        // overrides through unchanged. Empty string -> null so backend stores
        // SQL NULL; otherwise cast pack to Number for the JSON payload.
        const rawBaseUnit = item.base_unit;
        const baseUnit = typeof rawBaseUnit === 'string' && rawBaseUnit.trim() !== ''
          ? rawBaseUnit.trim()
          : null;
        const rawPack = item.base_units_per_pack;
        const baseUnitsPerPack = rawPack === '' || rawPack == null
          ? null
          : Number(rawPack);
        return {
          sku: item.sku || '',
          item_name: item.description || '',
          category_id: categoryId,
          quantity: parseFloat(item.quantity) || 0,
          unit_of_measure: item.unit_of_measure || '',
          unit_price: parseFloat(item.unit_price) || 0,
          total_price: totalPrice,
          base_unit: baseUnit,
          base_units_per_pack: baseUnitsPerPack,
        };
      });

      const invoiceData = {
        business_id: parseInt(selectedBusinessId),
        vendor_id: parseInt(selectedVendorId),
        invoice_number: data.invoice_number || '',
        invoice_date: data.invoice_date || '',
        notes: data.notes || '',
        tax: parseFloat(data.tax) || 0,
        fuel_surcharge: parseFloat(data.fuel_surcharge) || 0,
        fuel_surcharge_in_subtotal: fuelSurchargeInSubtotal,
        pdf_filename: filepath || '',
        line_items: lineItems,
        misc_charges: miscCharges,
      };

      const createResp = await api.createInvoice(invoiceData);
      const newSkusCount = createResp?.data?.new_skus_count || 0;
      setNewSkusCount(newSkusCount);
      setPackSizeWarnings(createResp?.data?.pack_size_warnings || []);

      // Remove queue item after successful save so it doesn't reappear in Review Next
      if (queueItemId) {
        try {
          await api.deleteQueueItem(queueItemId);
        } catch {
          // Non-critical: invoice was saved, queue cleanup is best-effort
        }
      }

      // Refresh categories in case new ones were created
      if (Object.keys(createdCategoryMap).length > 0) {
        const catRes = await api.getCategories();
        setCategories(catRes.data.filter(c => c.status === 'active'));
      }

      // Update vendor record with extracted details (address, fuel setting)
      if (selectedVendorId) {
        const vendor = vendors.find(v => v.id === parseInt(selectedVendorId));
        if (vendor) {
          const vendorUpdates = { ...vendor };
          let needsUpdate = false;

          // Save fuel surcharge setting if changed
          if (!!vendor.fuel_surcharge_in_subtotal !== fuelSurchargeInSubtotal) {
            vendorUpdates.fuel_surcharge_in_subtotal = fuelSurchargeInSubtotal;
            needsUpdate = true;
          }

          // Save extracted address and phone if vendor doesn't have them yet
          const va = extractedData?.vendor_address;
          if (va) {
            if (!vendor.address && !vendor.city) {
              if (va.street) { vendorUpdates.address = va.street; needsUpdate = true; }
              if (va.city) { vendorUpdates.city = va.city; needsUpdate = true; }
              if (va.state) { vendorUpdates.state = va.state; needsUpdate = true; }
              if (va.zip) { vendorUpdates.zip = va.zip; needsUpdate = true; }
            }
            if (va.phone && !vendor.primary_contact_phone) {
              vendorUpdates.primary_contact_phone = va.phone;
              needsUpdate = true;
            }
          }

          if (needsUpdate) {
            try {
              await api.updateVendor(parseInt(selectedVendorId), vendorUpdates);
            } catch {
              // Non-critical — vendor update failure shouldn't block invoice save
            }
          }
        }
      }

      setSaveSuccess(true);
      setToast({ message: 'Invoice saved successfully!', type: 'success' });
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Check for SKU catalog updates
      await checkSkuCatalogUpdates(lineItems);
    } catch (err) {
      const backendMsg = err?.response?.data?.error;
      const isDuplicate = backendMsg && backendMsg.toLowerCase().includes('already exists');
      if (isDuplicate) {
        setDuplicateError(true);
        const payload = err?.response?.data || {};
        const inCurrent = payload.duplicate_in_current_business;
        const extraNote = inCurrent === false
          ? ' The existing invoice belongs to a different business in your organization — switch businesses to view it.'
          : '';
        setValidationErrors([backendMsg + extraNote]);
        setToast({ message: 'Duplicate invoice detected', type: 'warning' });
        if (payload.existing_invoice_id) {
          setExistingInvoiceId(payload.existing_invoice_id);
        }
        if (typeof inCurrent === 'boolean') {
          setDuplicateInCurrentBusiness(inCurrent);
        }
        if (payload.existing_invoice_business_id != null) {
          setExistingInvoiceBusinessId(payload.existing_invoice_business_id);
        }
      } else {
        setValidationErrors([backendMsg || 'Failed to save invoice. Please try again.']);
        setToast({ message: 'Failed to save invoice', type: 'error' });
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  }, [data, newCategoryApprovals, selectedBusinessId, selectedVendorId, fuelSurchargeInSubtotal, validate, resolveCategoryId, checkSkuCatalogUpdates]);

  /**
   * Fetch the existing invoice and show the comparison view for appending.
   */
  const handleAppendToExisting = useCallback(async () => {
    if (!existingInvoiceId) return;
    try {
      const res = await api.getInvoice(existingInvoiceId);
      setExistingInvoice(res.data);
      setShowAppendComparison(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setToast({ message: 'Failed to load existing invoice', type: 'error' });
    }
  }, [existingInvoiceId]);

  /**
   * Confirm appending new line items to the existing invoice.
   */
  const handleAppendConfirm = useCallback(async () => {
    if (!existingInvoiceId || !data) return;
    setAppending(true);
    try {
      const lineItems = data.line_items.map(item => {
        const totalPrice = item.actual_total != null
          ? item.actual_total
          : (parseFloat(item.line_total) || (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0));
        // Phase 11 Wave 7 (NORM-05): pass per-line-item normalization
        // overrides through to the append endpoint.
        const rawBaseUnit = item.base_unit;
        const baseUnit = typeof rawBaseUnit === 'string' && rawBaseUnit.trim() !== ''
          ? rawBaseUnit.trim()
          : null;
        const rawPack = item.base_units_per_pack;
        const baseUnitsPerPack = rawPack === '' || rawPack == null
          ? null
          : Number(rawPack);
        return {
          sku: item.sku || '',
          item_name: item.description || '',
          category_id: item.category_id || null,
          quantity: parseFloat(item.quantity) || 0,
          unit_of_measure: item.unit_of_measure || '',
          unit_price: parseFloat(item.unit_price) || 0,
          total_price: totalPrice,
          base_unit: baseUnit,
          base_units_per_pack: baseUnitsPerPack,
        };
      });

      const miscCharges = (data.misc_charges || []).map(c => ({
        description: c.description,
        amount: parseFloat(c.amount) || 0,
      }));

      await api.appendToInvoice(existingInvoiceId, {
        line_items: lineItems,
        misc_charges: miscCharges,
        pdf_filename: queueFilename || '',
      });

      // Remove queue item after successful append
      if (queueItemId) {
        try {
          await api.deleteQueueItem(queueItemId);
        } catch {
          // Non-critical
        }
      }

      setSaveSuccess(true);
      setShowAppendComparison(false);
      setToast({ message: `${lineItems.length} line items appended to Invoice #${existingInvoice?.invoice_number || existingInvoiceId}`, type: 'success' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to append items';
      setToast({ message: msg, type: 'error' });
    } finally {
      setAppending(false);
    }
  }, [existingInvoiceId, data, queueFilename, queueItemId, existingInvoice]);

  // Success state — scroll to top so the success message is visible
  // IMPORTANT: This hook must be placed before any conditional returns to
  // satisfy React's rules of hooks (hooks cannot be called conditionally).
  useEffect(() => {
    if (saveSuccess) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [saveSuccess]);

  if (!data) return null;

  const categoryOptions = [
    ...categories.map(c => ({
      id: c.id,
      label: [c.level1, c.level2, c.level3].filter(Boolean).join(' > '),
      isNew: false,
    })),
    ...(data.new_categories || []).map((c, idx) => ({
      id: `new-${idx}`,
      label: [c.level1, c.level2, c.level3].filter(Boolean).join(' > '),
      isNew: true,
    })),
  ];

  if (saveSuccess) {
    const vendorDisplay = vendors.find(v => v.id === parseInt(selectedVendorId))?.name || vendorName || 'Unknown';
    const lineCount = data.line_items?.length || 0;
    const lineTotal = data.line_items?.reduce((sum, item) => {
      const amount = item.actual_total != null ? item.actual_total : (parseFloat(item.line_total) || 0);
      return sum + amount;
    }, 0) || 0;
    const miscTotal = (data.misc_charges || []).reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
    const grandTotal = lineTotal + miscTotal + (parseFloat(data.tax) || 0) + (parseFloat(data.fuel_surcharge) || 0);

    return (
      <div className="ocr-review__success-container">
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        <div className="ocr-review__success-message">
          &#10003; Invoice saved
        </div>
        <div className="card ocr-review__success-card">
          <div className="ocr-review__success-grid">
            <span className="ocr-review__success-label">Invoice #</span>
            <span className="ocr-review__success-value--bold">{data.invoice_number || '\u2014'}</span>
            <span className="ocr-review__success-label">Date</span>
            <span>{data.invoice_date || '\u2014'}</span>
            <span className="ocr-review__success-label">Vendor</span>
            <span>{vendorDisplay}</span>
            <span className="ocr-review__success-label">Line items</span>
            <span>{lineCount}</span>
            <span className="ocr-review__success-label">Total</span>
            <span className="ocr-review__success-value--bold">${grandTotal.toFixed(2)}</span>
            {newSkusCount > 0 && (
              <>
                <span className="ocr-review__success-label">SKU catalog</span>
                <span>{newSkusCount} new SKU{newSkusCount === 1 ? '' : 's'} added</span>
              </>
            )}
          </div>
        </div>
        {packSizeWarnings.length > 0 && (
          <div className="card" style={{ background: 'var(--amb50)', borderColor: 'var(--amb)', marginTop: 'var(--sp4)', padding: 'var(--sp4)' }} role="alert">
            <h3 style={{ marginTop: 0, marginBottom: 'var(--sp2)' }}>
              Pack size needed for {packSizeWarnings.filter(w => !w._fixed).length} item{packSizeWarnings.filter(w => !w._fixed).length === 1 ? '' : 's'}
            </h3>
            <p style={{ marginTop: 0, marginBottom: 'var(--sp3)' }}>
              These line items have a pack unit-of-measure (case, box, bag, etc.) that doesn't tell us how many base units are inside. Fix inline below, or visit <strong>Settings → SKU Catalog</strong> later.
            </p>
            <ul style={{ marginTop: 0, marginBottom: 0, paddingLeft: 'var(--sp4)' }}>
              {packSizeWarnings.map((w, i) => {
                const draftKey = w.sku || `__row_${i}`;
                const draft = packFixDrafts[draftKey] || { base_units_per_pack: '', base_unit: '' };
                const saving = !!packFixSaving[draftKey];
                if (w._fixed) {
                  return (
                    <li key={i} style={{ marginBottom: 'var(--sp2)', color: 'var(--g600)' }}>
                      ✓ <strong>{w.item_name}</strong>
                      {w.sku ? <> · SKU <span style={{ fontFamily: 'var(--font-m)' }}>{w.sku}</span></> : null}
                      <span style={{ marginLeft: 'var(--sp2)' }}>updated: {w._fixed.base_units_per_pack} {w._fixed.base_unit}</span>
                    </li>
                  );
                }
                return (
                  <li key={i} style={{ marginBottom: 'var(--sp3)' }}>
                    <div>
                      <strong>{w.item_name}</strong>
                      {w.sku ? <> · SKU <span style={{ fontFamily: 'var(--font-m)' }}>{w.sku}</span></> : null}
                      {w.unit_of_measure ? <> · UOM <em>{w.unit_of_measure}</em></> : null}
                    </div>
                    {w.sku && selectedVendorId ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp2)', alignItems: 'center', marginTop: 'var(--sp1)' }}>
                        <input
                          type="number"
                          step="any"
                          min="0"
                          placeholder="Units per pack (e.g. 24)"
                          aria-label={`Units per pack for ${w.item_name}`}
                          value={draft.base_units_per_pack}
                          onChange={(e) => setPackFixDrafts((d) => ({ ...d, [draftKey]: { ...draft, base_units_per_pack: e.target.value } }))}
                          style={{ width: '13em', padding: 'var(--sp1) var(--sp2)' }}
                          disabled={saving}
                        />
                        <select
                          aria-label={`Base unit for ${w.item_name}`}
                          value={draft.base_unit}
                          onChange={(e) => setPackFixDrafts((d) => ({ ...d, [draftKey]: { ...draft, base_unit: e.target.value } }))}
                          style={{ padding: 'var(--sp1) var(--sp2)' }}
                          disabled={saving}
                        >
                          <option value="">Base unit…</option>
                          <option value="each">each</option>
                          <option value="oz">oz</option>
                          <option value="fl_oz">fl oz</option>
                          <option value="lb">lb</option>
                          <option value="L">L (liter)</option>
                          <option value="ml">ml</option>
                          <option value="gal">gal</option>
                          <option value="kg">kg</option>
                          <option value="g">g</option>
                        </select>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          disabled={saving || !draft.base_units_per_pack || !draft.base_unit}
                          onClick={async () => {
                            setPackFixSaving((s) => ({ ...s, [draftKey]: true }));
                            try {
                              const resp = await api.setSKUPack({
                                vendor_id: parseInt(selectedVendorId, 10),
                                sku: w.sku,
                                base_unit: draft.base_unit,
                                base_units_per_pack: parseFloat(draft.base_units_per_pack),
                              });
                              setPackSizeWarnings((rows) => rows.map((row, idx) => idx === i
                                ? { ...row, _fixed: { base_unit: resp.data.base_unit, base_units_per_pack: resp.data.base_units_per_pack } }
                                : row));
                            } catch (err) {
                              setToast({ type: 'error', message: err.response?.data?.error || 'Failed to update pack size' });
                            } finally {
                              setPackFixSaving((s) => ({ ...s, [draftKey]: false }));
                            }
                          }}
                        >
                          {saving ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.85em', color: 'var(--st600)' }}>
                        (Item has no SKU or vendor — fix in Settings → SKU Catalog)
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {showSkuConfirm && skuUpdates.length > 0 && (
          <div className="card ocr-review__sku-confirm-card">
            <h3 className="ocr-review__sku-confirm-title">Update SKU Catalog Categories?</h3>
            <p className="ocr-review__sku-confirm-desc">
              The following SKUs have different categories in the catalog. Would you like to update them?
            </p>
            <table className="ocr-review__sku-table">
              <thead>
                <tr>
                  <th className="ocr-review__th">SKU</th>
                  <th className="ocr-review__th">Current Category</th>
                  <th className="ocr-review__th">New Category</th>
                </tr>
              </thead>
              <tbody>
                {skuUpdates.map((u, idx) => (
                  <tr key={idx}>
                    <td className="ocr-review__td">{u.sku}</td>
                    <td className="ocr-review__td">{u.oldCategoryLabel}</td>
                    <td className="ocr-review__td">{u.newCategoryLabel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="ocr-review__btn-row">
              <button type="button" onClick={handleApplySkuUpdates} disabled={updatingSkus}>
                {updatingSkus ? 'Updating...' : 'Update All'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => { setShowSkuConfirm(false); setSkuUpdates([]); }}>
                Skip
              </button>
            </div>
          </div>
        )}
        {onReviewNext ? (
          <div style={{ display: 'flex', gap: 'var(--sp3)', justifyContent: 'center', marginTop: 'var(--sp4)' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={async () => {
                if (loadingNext) return;
                setLoadingNext(true);
                try {
                  await onReviewNext();
                } finally {
                  // Note: we don't always get here because the parent typically
                  // unmounts this component when the next review opens. Setting
                  // false anyway in case onReviewNext fails fast and we stay.
                  setLoadingNext(false);
                }
              }}
              disabled={loadingNext}
              aria-busy={loadingNext}
            >
              {loadingNext ? 'Loading next…' : 'Review Next Invoice'}
            </button>
            {onCloseReview && (
              <button type="button" className="btn btn-secondary" onClick={onCloseReview}>
                Back to Queue
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--sp4)' }}>
            <button type="button" className="btn btn-primary" onClick={onBack}>
              Upload Another Invoice
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`ocr-review__layout ocr-review__layout--tab-${activeMobileTab}`}>
      {/* Re-extraction overlay — fixed to viewport so it's always visible.
          Triggered by the "Re-extract" button, the rotate handler's phase 1
          (rotating, fast), or phase 2 (rotatingReExtracting, slow). Mark UAT
          2026-05-26: rotate's small inline status was too easy to miss, so
          the full overlay now covers both rotate phases for continuous
          feedback from click → done. */}
      {(reExtracting || rotating || rotatingReExtracting) && (
        <div className="ocr-review__reextract-overlay" data-testid="reextract-overlay">
          <img src="/logos/loader/cogslog-loader-light-md.svg" width="48" height="108" alt="Loading..." />
          <p className="ocr-review__reextract-title" role="status" aria-live="polite">
            {rotating
              ? 'Rotating image…'
              : rotatingReExtracting
                ? 'Rotated. Re-extracting with COGSLOG…'
                : 'Re-extracting with COGSLOG…'}
          </p>
          <p className="ocr-review__reextract-subtitle">
            {rotating ? 'A few seconds…' : 'This may take 10–60 seconds'}
          </p>
        </div>
      )}

      {/* Mobile-only tab bar. Hidden on desktop via CSS (display: none above
          768px) so the existing two-column PDF+Form layout is unaffected.
          Tabs let phone users switch between Details (default), Items, and
          PDF without scrolling through ~3000px of content. */}
      <nav className="ocr-review__mobile-tabs" role="tablist" aria-label="Review sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeMobileTab === 'details'}
          className={`ocr-review__mobile-tab${activeMobileTab === 'details' ? ' ocr-review__mobile-tab--active' : ''}`}
          onClick={() => setActiveMobileTab('details')}
        >
          Details
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeMobileTab === 'items'}
          className={`ocr-review__mobile-tab${activeMobileTab === 'items' ? ' ocr-review__mobile-tab--active' : ''}`}
          onClick={() => setActiveMobileTab('items')}
        >
          Items
        </button>
        {(previewPages?.length > 0 || previewPageUrls?.length > 0 || previewImage) && (
          <button
            type="button"
            role="tab"
            aria-selected={activeMobileTab === 'pdf'}
            className={`ocr-review__mobile-tab${activeMobileTab === 'pdf' ? ' ocr-review__mobile-tab--active' : ''}`}
            onClick={() => setActiveMobileTab('pdf')}
          >
            PDF
          </button>
        )}
      </nav>

      {/* Left: PDF Preview. Also serves as the PDF tab pane on mobile —
          hidden unless active tab is 'pdf' (handled by CSS targeting
          .ocr-review__layout--tab-pdf .ocr-review__tab-pane--pdf). */}
      {(previewPages?.length > 0 || previewPageUrls?.length > 0 || previewImage || localPreviewImage) && (
        <div className="ocr-review__preview-column ocr-review__tab-pane--pdf">
          <div className="card ocr-review__preview-card">
            <h3 className="ocr-review__section-title">
              PDF Preview {(() => {
                const n = previewPageUrls?.length || previewPages?.length || 0;
                return n > 1 ? `(${n} pages)` : '';
              })()}
              <span className="ocr-review__zoom-controls">
                {/* Rotate buttons (Mark UAT 2026-05-26). Disabled when
                    there's no queue item to operate on (e.g. the legacy
                    direct-upload flow), or while a rotation is in flight. */}
                {queueItemId && (
                  <>
                    <button
                      type="button"
                      className="ocr-review__zoom-btn"
                      onClick={() => handleRotate('left')}
                      title="Rotate 90° counter-clockwise"
                      aria-label="Rotate counter-clockwise"
                      disabled={rotating}
                    >
                      ↺
                    </button>
                    <button
                      type="button"
                      className="ocr-review__zoom-btn"
                      onClick={() => handleRotate('right')}
                      title="Rotate 90° clockwise"
                      aria-label="Rotate clockwise"
                      disabled={rotating}
                    >
                      ↻
                    </button>
                  </>
                )}
                <button type="button" className="ocr-review__zoom-btn" onClick={() => setPreviewZoom(prev => Math.max(1, prev - 0.5))} title="Zoom out" disabled={previewZoom <= 1}>−</button>
                <span className="ocr-review__zoom-level">{Math.round(previewZoom * 100)}%</span>
                <button type="button" className="ocr-review__zoom-btn" onClick={() => setPreviewZoom(prev => Math.min(4, prev + 0.5))} title="Zoom in">+</button>
              </span>
            </h3>
            <div className="ocr-review__preview-scroll">
              {/* localPreviewImage takes precedence after a rotate so the
                  user sees the new orientation without waiting for the
                  parent to re-fetch. Multi-page invoices fall back to
                  previewPages since rotate only returns the first page.

                  Backend (2026-08-27) now returns page 1 base64-inline and
                  pages 2..N as URLs in previewPageUrls (routes/ocr.py
                  ocr_queue_preview). Lazy-loading them here cuts the
                  upfront wait from ~10 s to ~2 s. Fallback to old-shape
                  previewPages (all base64) keeps unpatched backends
                  working. */}
              {(() => {
                // Choose the source list to render. After a rotate,
                // localPreviewImage overrides everything.
                if (localPreviewImage) {
                  return [{ b64: localPreviewImage, url: null }];
                }
                // Prefer the URL list (lazy) when the backend returned one.
                if (previewPageUrls?.length > 0) {
                  return previewPageUrls.map((u, i) => ({
                    // Page 1 uses the inlined base64 so it renders instantly
                    // — the URL fetch would go through the network for no
                    // reason.
                    b64: i === 0 ? (previewImage || null) : null,
                    url: i === 0 ? null : u,
                  }));
                }
                // Legacy shape: previewPages is a full base64 array.
                if (previewPages?.length > 0) {
                  return previewPages.map(b64 => ({ b64, url: null }));
                }
                return previewImage ? [{ b64: previewImage, url: null }] : [];
              })().map((page, idx) => (
                <img
                  key={idx}
                  ref={idx === 0 ? previewImageRef : undefined}
                  // Backend switched inline base64 from PNG → JPEG on
                  // 2026-08-27 to cut payload ~3-5× on scan-heavy invoices.
                  // Browsers sniff magic bytes and would render either, but
                  // the correct mime hint spares that guess.
                  src={page.url || `data:image/jpeg;base64,${page.b64}`}
                  alt={`Invoice page ${idx + 1}`}
                  loading={idx === 0 ? 'eager' : 'lazy'}
                  className="ocr-review__preview-img"
                  style={{ width: `${(previewZoom || 1) * 100}%`, minWidth: `${(previewZoom || 1) * 100}%` }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Right: Editable Form */}
      <div className="ocr-review__form-column">
        {/* Confidence Badge */}
        <div className="ocr-review__confidence-badge">
          <span className={`ocr-review__confidence-pill ocr-review__confidence-pill--${data.confidence === 'high' ? 'high' : data.confidence === 'medium' ? 'medium' : 'low'}`}>
            {data.confidence?.toUpperCase()} confidence
          </span>
          {data.notes && (
            <span className="ocr-review__confidence-note">
              {showConfidenceDetails
                ? data.notes
                : (data.notes.includes('. ') ? data.notes.split('. ')[0] + '.' : data.notes.substring(0, 80))}
              {(data.notes.includes('. ') || data.notes.length > 80) && (
                <button
                  type="button"
                  onClick={() => setShowConfidenceDetails(prev => !prev)}
                  className="ocr-review__confidence-toggle"
                >
                  {showConfidenceDetails ? 'Hide details' : 'Show details'}
                </button>
              )}
            </span>
          )}
        </div>

        {/* Toast Notifications */}
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

        {/* Validation Errors (non-duplicate) */}
        {validationErrors.length > 0 && !duplicateError && (
          <div className="ocr-review__validation-box ocr-review__validation-box--error">
            {validationErrors.map((err, i) => (
              <p key={i} className="ocr-review__validation-msg ocr-review__validation-msg--error">{err}</p>
            ))}
          </div>
        )}

        {/* Duplicate Invoice Modal */}
        <Modal
          isOpen={duplicateError && !showExistingInvoice}
          onClose={() => {
            setDuplicateError(false);
            setValidationErrors([]);
          }}
          title="Duplicate Invoice Found"
          size="sm"
        >
          <div style={{ marginBottom: 'var(--sp4)' }}>
            <p style={{ margin: '0 0 var(--sp3)', color: 'var(--st700)' }}>
              {validationErrors[0] || 'An invoice with this number already exists for this vendor.'}
            </p>
            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--st500)' }}>
              You can change the invoice number, view the existing invoice, or cancel and go back.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp2)', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }} onClick={async () => {
              setDuplicateError(false);
              setValidationErrors([]);
              if (queueItemId && onDeleteFromQueue) {
                await onDeleteFromQueue();
              } else if (onBack) {
                onBack();
              }
            }}>
              Delete
            </button>
            {existingInvoiceId && duplicateInCurrentBusiness !== false && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => {
                setShowExistingInvoice(true);
              }}>
                View Existing
              </button>
            )}
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => {
              setDuplicateError(false);
              setValidationErrors([]);
              setExistingInvoiceId(null);
              setEditingDetails(true);
            }}>
              Change Invoice #
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => {
              setDuplicateError(false);
              setValidationErrors([]);
            }}>
              Cancel
            </button>
          </div>
        </Modal>


        {/* Invoice Details — read-first card with edit toggle.
            On mobile this section is wrapped in tab-pane--details so it
            only shows when the Details tab is active. Desktop ignores
            tab-pane classes — both sections render side-by-side in the
            two-column layout via .ocr-review__details-columns. */}
        {!showAppendComparison && (<><div className="ocr-review__tab-pane--details"><div className="card ocr-review__card--mb ocr-review__details-card">
          <div className="ocr-review__details-columns">
            {/* Left: Invoice fields */}
            <div className="ocr-review__details-left">
              <div className="ocr-review__details-title-row">
                <span className="ocr-review__details-overline">INVOICE</span>
                {editingDetails
                  ? <button type="button" className="btn btn-primary btn-xs" onClick={() => setEditingDetails(false)}>Done</button>
                  : <button type="button" className="btn btn-ghost btn-xs" onClick={() => setEditingDetails(true)}>Edit</button>
                }
              </div>
              <span className="ocr-review__details-invoice-num">
                {editingDetails
                  ? <input id="review-invoice-num" ref={invoiceNumRef} type="text" className="input ocr-review__details-input" value={data.invoice_number || ''} onChange={e => updateField('invoice_number', e.target.value)} placeholder="Invoice #" />
                  : (data.invoice_number || '—')
                }
              </span>

              <div className="ocr-review__details-kv">
                <span className="ocr-review__details-label">Date</span>
                {editingDetails
                  ? <input id="review-date" type="date" className="input ocr-review__details-input" value={data.invoice_date || ''} onChange={e => updateField('invoice_date', e.target.value)} />
                  : <span className="ocr-review__details-value">{data.invoice_date || '—'}</span>
                }
              </div>

              <div className="ocr-review__details-divider"></div>

              {/* Per-field uncertainty markers (Ruth UAT 2026-05-26).
                  `uncertain_totals` is a Claude-returned array of field
                  names it was unsure about. Show a small "?" next to
                  each so the user knows what to spot-check. */}
              {(() => null)()}
              <div className="ocr-review__details-kv">
                <span className="ocr-review__details-label">
                  Subtotal
                  {Array.isArray(data.uncertain_totals) && data.uncertain_totals.includes('subtotal') && (
                    <span className="ocr-review__uncertain-marker" title="Claude was unsure about this value — please verify against the invoice">&nbsp;⚠</span>
                  )}
                </span>
                {editingDetails
                  ? <input id="review-subtotal" type="number" step="0.01" className="input ocr-review__details-input ocr-review__details-input--mono" value={data.subtotal || ''} onChange={e => updateField('subtotal', e.target.value)} />
                  : <span className="ocr-review__details-value ocr-review__details-value--mono">${(parseFloat(data.subtotal) || 0).toFixed(2)}</span>
                }
              </div>
              <div className="ocr-review__details-kv">
                <span className="ocr-review__details-label">
                  Tax
                  {Array.isArray(data.uncertain_totals) && data.uncertain_totals.includes('tax') && (
                    <span className="ocr-review__uncertain-marker" title="Claude was unsure about this value — please verify against the invoice">&nbsp;⚠</span>
                  )}
                </span>
                {editingDetails
                  ? <input id="review-tax" type="number" step="0.01" className="input ocr-review__details-input ocr-review__details-input--mono" value={data.tax || ''} onChange={e => updateField('tax', e.target.value)} />
                  : <span className="ocr-review__details-value ocr-review__details-value--mono">${(parseFloat(data.tax) || 0).toFixed(2)}</span>
                }
              </div>
              <div className="ocr-review__details-kv">
                <span className="ocr-review__details-label">
                  Fuel Surcharge
                  {Array.isArray(data.uncertain_totals) && data.uncertain_totals.includes('fuel_surcharge') && (
                    <span className="ocr-review__uncertain-marker" title="Claude was unsure about this value — please verify against the invoice">&nbsp;⚠</span>
                  )}
                </span>
                <div className="ocr-review__details-fuel">
                  {editingDetails
                    ? <input id="review-fuel-surcharge" type="number" step="0.01" className="input ocr-review__details-input ocr-review__details-input--mono" value={data.fuel_surcharge || ''} onChange={e => updateField('fuel_surcharge', e.target.value)} />
                    : <span className="ocr-review__details-value ocr-review__details-value--mono">${(parseFloat(data.fuel_surcharge) || 0).toFixed(2)}</span>
                  }
                  <label className="ocr-review__details-checkbox">
                    <input type="checkbox" checked={fuelSurchargeInSubtotal} onChange={e => setFuelSurchargeInSubtotal(e.target.checked)} />
                    In subtotal
                  </label>
                </div>
              </div>

              <div className="ocr-review__details-kv ocr-review__details-kv--total">
                <span className="ocr-review__details-label">
                  Total
                  {Array.isArray(data.uncertain_totals) && data.uncertain_totals.includes('total_amount') && (
                    <span className="ocr-review__uncertain-marker" title="Claude was unsure about this value — please verify against the invoice">&nbsp;⚠</span>
                  )}
                </span>
                {editingDetails
                  ? <input id="review-total" type="number" step="0.01" className="input ocr-review__details-input ocr-review__details-input--mono ocr-review__details-input--bold" value={data.total_amount || ''} onChange={e => updateField('total_amount', e.target.value)} />
                  : <span className="ocr-review__details-total">${(parseFloat(data.total_amount) || 0).toFixed(2)}</span>
                }
              </div>

              {/* Calculated vs Extracted Subtotal Comparison */}
              {(() => {
                const lineItemSum = (data.line_items || []).reduce((sum, item) => {
                  const t = item.actual_total != null ? item.actual_total : (parseFloat(item.line_total) || 0);
                  return sum + t;
                }, 0);
                const fuel = parseFloat(data.fuel_surcharge || 0);
                const miscSum = (data.misc_charges || []).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
                const calcTotal = fuelSurchargeInSubtotal
                  ? lineItemSum + fuel + miscSum
                  : lineItemSum + miscSum;
                const invoiceSubtotal = parseFloat(data.subtotal || 0);
                const diff = Math.abs(calcTotal - invoiceSubtotal);
                return (
                  <div className="ocr-review__subtotal-compare">
                    <span className="ocr-review__subtotal-item">
                      <span className="ocr-review__subtotal-label">Line Items:</span>
                      <span className="td-amt">${lineItemSum.toFixed(2)}</span>
                    </span>
                    {fuel > 0 && (
                      <span className="ocr-review__subtotal-item">
                        <span className="ocr-review__subtotal-label">Fuel{fuelSurchargeInSubtotal ? ' (in subtotal)' : ''}:</span>
                        <span className="td-amt">${fuel.toFixed(2)}</span>
                      </span>
                    )}
                    {miscSum > 0 && (
                      <span className="ocr-review__subtotal-item">
                        <span className="ocr-review__subtotal-label">Misc Charges:</span>
                        <span className="td-amt">${miscSum.toFixed(2)}</span>
                      </span>
                    )}
                    <span className="ocr-review__subtotal-item">
                      <span className="ocr-review__subtotal-label">Calculated:</span>
                      <span className="td-amt" style={{ fontWeight: 700 }}>${calcTotal.toFixed(2)}</span>
                    </span>
                    <span className="ocr-review__subtotal-item">
                      <span className="ocr-review__subtotal-label">Invoice Subtotal:</span>
                      <span className="td-amt" style={{ fontWeight: 700 }}>${invoiceSubtotal.toFixed(2)}</span>
                    </span>
                    {diff > 0.50 && (
                      <span className="ocr-review__subtotal-item ocr-review__subtotal-item--error">
                        &#9888; Difference: ${diff.toFixed(2)}
                      </span>
                    )}
                    {diff <= 0.50 && (
                      <span className="ocr-review__subtotal-item ocr-review__subtotal-item--ok">
                        &#10003; Totals match
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Right: Business + Vendor details */}
            <div className="ocr-review__details-right">
              <span className="ocr-review__details-overline">BUSINESS</span>
              <select
                id="review-business-select"
                className="input ocr-review__details-select"
                value={selectedBusinessId}
                onChange={e => setSelectedBusinessId(e.target.value)}
                aria-label="Select business for this invoice"
              >
                <option value="">Select business...</option>
                {businesses.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>

              <div className="ocr-review__details-divider"></div>

              <span className="ocr-review__details-overline">VENDOR</span>
              {!changingVendor && vendorId ? (
                <div className="ocr-review__details-vendor-info">
                  <span className="ocr-review__details-vendor-name">
                    {/* Mark UAT 2026-05-27 (GH #147): when selectedVendorId resolves to
                        a real vendor record, prefer that name over the raw `vendorName`
                        prop. The prop carries the original (possibly multi-vendor / messy)
                        extraction string and shouldn't override a user-confirmed choice. */}
                    {vendors.find(v => v.id === parseInt(selectedVendorId))?.name || vendorName || `Vendor #${selectedVendorId}`}
                  </span>
                  {(() => {
                    const v = vendors.find(v => v.id === parseInt(selectedVendorId));
                    const hasStoredAddress = v && (v.address || v.city);
                    const hasExtractedAddress = extractedData && extractedData.vendor_address;
                    if (hasStoredAddress) {
                      const phone = v.primary_contact_phone || (hasExtractedAddress && extractedData.vendor_address?.phone);
                      return (
                        <div className="ocr-review__details-vendor-address">
                          {v.address && <span>{v.address}</span>}
                          {(v.city || v.state || v.zip) && (
                            <span>{[v.city, v.state].filter(Boolean).join(', ')}{v.zip ? ` ${v.zip}` : ''}</span>
                          )}
                          {phone && <span>{phone}</span>}
                        </div>
                      );
                    } else if (hasExtractedAddress) {
                      const va = extractedData.vendor_address;
                      const phone = va.phone || v?.primary_contact_phone;
                      return (
                        <div className="ocr-review__details-vendor-address">
                          {va.street && <span>{va.street}</span>}
                          {(va.city || va.state || va.zip) && (
                            <span>{[va.city, va.state].filter(Boolean).join(', ')}{va.zip ? ` ${va.zip}` : ''}</span>
                          )}
                          {phone && <span>{phone}</span>}
                          <span className="ocr-review__details-tag">(from invoice)</span>
                        </div>
                      );
                    } else if (v?.primary_contact_phone) {
                      return (
                        <div className="ocr-review__details-vendor-address">
                          <span>{v.primary_contact_phone}</span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  <button type="button" onClick={() => setChangingVendor(true)} className="ocr-review__btn--change">Change</button>
                </div>
              ) : (
                <select
                  id="review-vendor-select"
                  className="input ocr-review__details-select"
                  value={selectedVendorId}
                  onChange={e => {
                    if (e.target.value === '__add_new__') {
                      setShowVendorModal(true);
                    } else {
                      setSelectedVendorId(e.target.value);
                    }
                  }}
                >
                  <option value="">Select vendor...</option>
                  {vendors.map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                  <option value="__add_new__">+ Add New Vendor...</option>
                </select>
              )}
            </div>
          </div>
        </div>

        {/* New Category Confirmation */}
        {data.new_categories && data.new_categories.length > 0 && (
          <div className="card ocr-review__new-cat-card">
            <h3 className="ocr-review__section-title--tight">New Categories Suggested</h3>
            <p className="ocr-review__new-cat-desc">
              COGSLOG suggests creating these categories. Uncheck to reject.
            </p>
            {data.new_categories.map((cat, idx) => (
              <label key={idx} className="ocr-review__new-cat-label">
                <input
                  type="checkbox"
                  checked={!!newCategoryApprovals[idx]}
                  onChange={e => setNewCategoryApprovals(prev => ({ ...prev, [idx]: e.target.checked }))}
                />
                <span>{[cat.level1, cat.level2, cat.level3].filter(Boolean).join(' > ')}</span>
                <span className="ocr-review__new-cat-badge">NEW</span>
              </label>
            ))}
            <button
              type="button"
              className="btn btn-primary"
              style={{ marginTop: 'var(--sp4)' }}
              onClick={async () => {
                const newCats = data.new_categories || [];
                let created = 0;
                const updatedCategories = [...categories];
                const updatedLineItems = [...(data.line_items || [])];

                for (let i = 0; i < newCats.length; i++) {
                  if (!newCategoryApprovals[i]) continue;
                  try {
                    const response = await api.createCategory(newCats[i]);
                    const newId = response.data.id;
                    updatedCategories.push({ id: newId, ...newCats[i], status: 'active' });

                    // Apply to matching line items
                    updatedLineItems.forEach((item, idx) => {
                      if (item.suggested_category?._newKey === `new-${i}` ||
                          (item.suggested_category?.is_new &&
                           item.suggested_category?.level1 === newCats[i].level1 &&
                           item.suggested_category?.level2 === newCats[i].level2 &&
                           item.suggested_category?.level3 === newCats[i].level3)) {
                        updatedLineItems[idx] = {
                          ...item,
                          suggested_category: { category_id: newId, is_new: false, _newKey: undefined },
                        };
                      }
                    });
                    created++;
                  } catch {
                    // Category may already exist
                  }
                }

                setCategories(updatedCategories);
                setData(prev => ({
                  ...prev,
                  line_items: updatedLineItems,
                  new_categories: [],
                }));
                setNewCategoryApprovals({});
                if (created > 0) {
                  setToast({ message: `Created ${created} ${created === 1 ? 'category' : 'categories'} and applied to line items`, type: 'success' });
                }
              }}
            >
              Create All &amp; Apply to Line Items
            </button>
          </div>
        )}

        {/* Image-quality warning banner — appears for blurry photo uploads.
            Set at upload time by routes/ocr.py based on the Laplacian
            variance of the source image. NULL on PDF uploads. */}
        {imageQualityWarning && (
          <div className="card ocr-review__card--mb" data-testid="image-quality-warning" style={{
            background: 'var(--amb50)',
            border: '1px solid var(--amb)'
          }}>
            <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--amb700)' }}>
              &#128247; <strong>Low image quality:</strong> {imageQualityWarning}
            </p>
          </div>
        )}

        {/* Subtotal Mismatch Warning — hide when fuel surcharge explains the difference */}
        {data._subtotal_mismatch && !fuelSurchargeInSubtotal && (
          <div className="card ocr-review__card--mb" style={{
            background: 'var(--amb50)',
            border: '1px solid var(--amb)'
          }}>
            <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--amb700)' }}>
              &#9888; <strong>Subtotal Mismatch:</strong> {data._subtotal_mismatch.message}
            </p>
          </div>
        )}

        {/* Validation Summary Banner */}
        {data._validation_summary && (data._validation_summary.corrected > 0 || data._validation_summary.flagged > 0) && (
          <div className="card ocr-review__card--mb" style={{
            background: data._validation_summary.flagged > 0 ? 'var(--amb50)' : 'var(--g50)',
            border: `1px solid ${data._validation_summary.flagged > 0 ? 'var(--amb)' : 'var(--g200)'}`
          }}>
            <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
              {data._validation_summary.corrected > 0 && (
                <span style={{ color: 'var(--g600)', marginRight: 'var(--sp4)' }}>
                  &#10003; {data._validation_summary.corrected} {data._validation_summary.corrected === 1 ? 'item' : 'items'} auto-corrected
                </span>
              )}
              {data._validation_summary.flagged > 0 && (
                <span style={{ color: 'var(--amb700)' }}>
                  &#9888; {data._validation_summary.flagged} {data._validation_summary.flagged === 1 ? 'item needs' : 'items need'} manual review
                </span>
              )}
            </p>
          </div>
        )}

        {/* End of Details tab pane (mobile). Subtotal mismatch + validation
            summary stay inside Details because they describe totals that are
            edited from this tab — putting them above the tabs would make them
            permanently occupy vertical space on Items and PDF tabs too. */}
        </div>

        {/* Items tab pane — line items + misc charges. */}
        <div className="ocr-review__tab-pane--items">

        {/* Line Items Table */}
        <div className="card ocr-review__card--mb">
          <div className="settings-form__table-header">
            <h3 className="settings-form__section-title" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
              Line Items ({data.line_items?.length || 0}){(data.misc_charges || []).length > 0 ? ` + ${(data.misc_charges || []).length} charge${(data.misc_charges || []).length !== 1 ? 's' : ''}` : ''}
            </h3>
            <div style={{ display: 'flex', gap: 'var(--sp2)' }}>
              <button type="button" onClick={() => { setNewLineForm({ sku: '', description: '', quantity: 1, unit_of_measure: '', unit_price: 0 }); setShowAddLineModal(true); }} className="btn btn-primary btn-sm">
                + Add Item
              </button>
              <button type="button" onClick={() => { setNewMiscForm({ description: '', amount: 0 }); setShowAddMiscModal(true); }} className="btn btn-secondary btn-sm">
                + Add Charge
              </button>
            </div>
          </div>
          <div className="tbl-wrap">
            <table className="ocr-review__line-table">
              <thead>
                <tr>
                  <th scope="col" className="ocr-review__line-th">#</th>
                  {vendorUsesSku && <th scope="col" className="ocr-review__line-th">SKU</th>}
                  <th scope="col" className="ocr-review__line-th ocr-review__line-th--desc">Description</th>
                  <th scope="col" className="ocr-review__line-th ocr-review__line-th--right">Qty</th>
                  <th scope="col" className="ocr-review__line-th">UoM</th>
                  <th scope="col" className="ocr-review__line-th ocr-review__line-th--right">Price</th>
                  <th scope="col" className="ocr-review__line-th ocr-review__line-th--right">Total</th>
                  <th scope="col" className="ocr-review__line-th">Category</th>
                  <th scope="col" className="ocr-review__line-th ocr-review__line-th--action"></th>
                </tr>
              </thead>
              <tbody>
                {(data.line_items || []).map((item, idx) => {
                  const calculated = Math.round((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0) * 100) / 100;
                  const actual = item.actual_total != null ? item.actual_total : null;
                  const hasDiscrepancy = actual != null && Math.abs(actual - calculated) > 0.01;
                  const displayAmount = actual != null ? actual : (parseFloat(item.line_total) || 0);
                  const catLabel = (() => {
                    const catId = item.suggested_category?.category_id;
                    const newKey = item.suggested_category?._newKey;
                    if (newKey) {
                      const opt = categoryOptions.find(o => String(o.id) === String(newKey));
                      return opt ? `[NEW] ${opt.label}` : '—';
                    }
                    if (catId) {
                      const opt = categoryOptions.find(o => String(o.id) === String(catId));
                      return opt ? opt.label : '—';
                    }
                    return '—';
                  })();

                  return (
                    <tr key={idx} className={`ocr-review__line-row ${item._flagged ? 'ocr-review__row--flagged' : ''} ${item._corrected ? 'ocr-review__row--corrected' : ''}`}>
                      <td className="ocr-review__line-td ocr-review__line-td--num">{idx + 1}</td>
                      {vendorUsesSku && (
                        <td className="ocr-review__line-td">
                          <span className="ocr-review__line-text ocr-review__line-text--mono">{item.sku || '—'}</span>
                        </td>
                      )}
                      <td className="ocr-review__line-td">
                        <span className="ocr-review__line-text">{item.description || '—'}</span>
                        {item._corrected && <span className="ocr-review__correction-badge" title={item._correction_note || 'Auto-corrected'}>&#10003;</span>}
                        {item._flagged && <span className="ocr-review__flag-badge" title={item._flag_reason || 'Needs review'}>&#9888;</span>}
                      </td>
                      <td className="ocr-review__line-td ocr-review__line-td--right">
                        <span className="ocr-review__line-text ocr-review__line-text--mono">{item.quantity ?? '—'}</span>
                      </td>
                      <td className="ocr-review__line-td">
                        <span className="ocr-review__line-text">{item.unit_of_measure || '—'}</span>
                      </td>
                      <td className="ocr-review__line-td ocr-review__line-td--right">
                        <span className="td-amt">${(parseFloat(item.unit_price) || 0).toFixed(2)}</span>
                      </td>
                      <td className="ocr-review__line-td ocr-review__line-td--right">
                        {hasDiscrepancy ? (
                          <span className="td-amt ocr-review__total--discrepancy" title={`Calculated: $${calculated.toFixed(2)} | Actual: $${actual.toFixed(2)}`}>
                            ${displayAmount.toFixed(2)} <span className="ocr-review__discrepancy-icon">!</span>
                          </span>
                        ) : (
                          <span className="td-amt">${displayAmount.toFixed(2)}</span>
                        )}
                      </td>
                      <td className="ocr-review__line-td">
                        <span className="ocr-review__line-text ocr-review__line-text--muted">{catLabel}</span>
                      </td>
                      <td className="ocr-review__line-td ocr-review__line-td--action">
                        <div className="ocr-review__action-wrap" ref={actionMenuIdx === idx ? actionMenuRef : null}>
                          <button
                            type="button"
                            className="ocr-review__action-btn"
                            onClick={() => setActionMenuIdx(actionMenuIdx === idx ? null : idx)}
                            aria-label="Actions"
                            aria-haspopup="true"
                            aria-expanded={actionMenuIdx === idx}
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                              <circle cx="8" cy="3" r="1.5" />
                              <circle cx="8" cy="8" r="1.5" />
                              <circle cx="8" cy="13" r="1.5" />
                            </svg>
                          </button>
                          {actionMenuIdx === idx && (
                            <div className="ocr-review__action-menu" role="menu">
                              <button type="button" className="ocr-review__action-item" role="menuitem" onClick={() => { setEditingRowIdx(idx); setActionMenuIdx(null); }}>Edit</button>
                              <button type="button" className="ocr-review__action-item ocr-review__action-item--danger" role="menuitem" onClick={() => { removeLineItem(idx); setActionMenuIdx(null); }}>Delete</button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add Line Item Modal */}
        <Modal
          isOpen={showAddLineModal}
          onClose={() => setShowAddLineModal(false)}
          title="Add Line Item"
          size="md"
        >
          <form onSubmit={(e) => { e.preventDefault(); addLineItemFromForm(); }}>
            {vendorUsesSku && (
              <div className="field">
                <label className="label">SKU</label>
                <input className="input" type="text" value={newLineForm.sku} onChange={e => setNewLineForm({ ...newLineForm, sku: e.target.value })} style={{ fontFamily: 'var(--font-m)' }} />
              </div>
            )}
            <div className="field">
              <label className="label">Description <span className="req">*</span></label>
              <input className="input" type="text" value={newLineForm.description} onChange={e => setNewLineForm({ ...newLineForm, description: e.target.value })} required />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--sp3)' }}>
              <div className="field">
                <label className="label">Quantity</label>
                <input className="input" type="number" step="0.01" value={newLineForm.quantity} onChange={e => setNewLineForm({ ...newLineForm, quantity: e.target.value })} style={{ fontFamily: 'var(--font-m)' }} />
              </div>
              <div className="field">
                <label className="label">UOM</label>
                <input className="input" type="text" value={newLineForm.unit_of_measure} onChange={e => setNewLineForm({ ...newLineForm, unit_of_measure: e.target.value })} />
              </div>
              <div className="field">
                <label className="label">Unit Price</label>
                <input className="input" type="number" step="0.01" value={newLineForm.unit_price} onChange={e => setNewLineForm({ ...newLineForm, unit_price: e.target.value })} style={{ fontFamily: 'var(--font-m)' }} />
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: '0.9rem', marginBottom: 'var(--sp3)' }}>
              <span style={{ color: 'var(--st500)' }}>Total: </span>
              <span className="td-amt" style={{ fontWeight: 600 }}>${((parseFloat(newLineForm.quantity) || 0) * (parseFloat(newLineForm.unit_price) || 0)).toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', gap: 'var(--sp2)' }}>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>Add Item</button>
              <button type="button" className="btn btn-ghost" onClick={() => setShowAddLineModal(false)}>Cancel</button>
            </div>
          </form>
        </Modal>

        {/* Add Misc Charge Modal */}
        <Modal
          isOpen={showAddMiscModal}
          onClose={() => setShowAddMiscModal(false)}
          title="Add Miscellaneous Charge"
          size="sm"
        >
          <form onSubmit={(e) => {
            e.preventDefault();
            setData(prev => ({
              ...prev,
              misc_charges: [...(prev.misc_charges || []), { ...newMiscForm }]
            }));
            setShowAddMiscModal(false);
            setIsDirty(true);
          }}>
            <div className="field">
              <label className="label">Description <span className="req">*</span></label>
              <input className="input" type="text" value={newMiscForm.description} onChange={e => setNewMiscForm({ ...newMiscForm, description: e.target.value })} placeholder="e.g., Delivery Fee" required />
            </div>
            <div className="field">
              <label className="label">Amount <span className="req">*</span></label>
              <input className="input" type="number" step="0.01" min="0" value={newMiscForm.amount} onChange={e => setNewMiscForm({ ...newMiscForm, amount: e.target.value })} style={{ fontFamily: 'var(--font-m)' }} required />
            </div>
            <div style={{ display: 'flex', gap: 'var(--sp2)', marginTop: 'var(--sp4)' }}>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>Add Charge</button>
              <button type="button" className="btn btn-ghost" onClick={() => setShowAddMiscModal(false)}>Cancel</button>
            </div>
          </form>
        </Modal>

        {/* Edit Line Item Modal */}
        {editingRowIdx != null && data.line_items?.[editingRowIdx] && (
          <Modal
            isOpen={true}
            onClose={() => { setEditingRowIdx(null); setAddingCategoryForRow(null); }}
            title={`Edit Line Item #${editingRowIdx + 1}`}
            size="md"
          >
            {(() => {
              const item = data.line_items[editingRowIdx];
              const idx = editingRowIdx;
              return (
                <div>
                  {vendorUsesSku && (
                    <div className="field">
                      <label className="label">SKU</label>
                      <input type="text" className="input" style={{ fontFamily: 'var(--font-m)' }} value={item.sku || ''} onChange={e => updateLineItem(idx, 'sku', e.target.value)} />
                    </div>
                  )}
                  <div className="field">
                    <label className="label">Description</label>
                    <input type="text" className="input" value={item.description || ''} onChange={e => updateLineItem(idx, 'description', e.target.value)} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--sp3)' }}>
                    <div className="field">
                      <label className="label">Quantity</label>
                      <input type="number" step="0.01" className="input" style={{ fontFamily: 'var(--font-m)' }} value={item.quantity || ''} onChange={e => updateLineItem(idx, 'quantity', e.target.value)} />
                    </div>
                    <div className="field">
                      <label className="label">Unit of Measure</label>
                      <input type="text" className="input" value={item.unit_of_measure || ''} onChange={e => updateLineItem(idx, 'unit_of_measure', e.target.value)} />
                    </div>
                    <div className="field">
                      <label className="label">Unit Price</label>
                      <input type="number" step="0.01" className="input" style={{ fontFamily: 'var(--font-m)' }} value={item.unit_price || ''} onChange={e => updateLineItem(idx, 'unit_price', e.target.value)} />
                    </div>
                  </div>

                  {/* Phase 11.1: per-line-item override fields hidden behind
                      an "Advanced" toggle. The system auto-derives the
                      canonical unit from `unit_of_measure` (e.g. "5 LB" → 80
                      oz) for ~90% of cases. Operators only open this when
                      the line UOM is a pack ("case", "box") and the pack
                      size has to be specified per-line. */}
                  <details style={{ marginBottom: 'var(--sp3)' }}>
                    <summary className="hint" style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Override price normalization (advanced)
                    </summary>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp3)', marginTop: 'var(--sp2)' }}>
                    <div className="field">
                      <label className="label" htmlFor={`ocr-edit-base-unit-${idx}`}>Base unit (override)</label>
                      {(() => {
                        const isCustom = baseUnitCustomByRow[idx]
                          || (Boolean(item.base_unit) && !BASE_UNIT_OPTIONS.includes(item.base_unit));
                        const selectVal = isCustom ? BASE_UNIT_NEW_SENTINEL : (item.base_unit || '');
                        const handleSelect = (val) => {
                          if (val === BASE_UNIT_NEW_SENTINEL) {
                            setBaseUnitCustomByRow(prev => ({ ...prev, [idx]: true }));
                            updateLineItem(idx, 'base_unit', '');
                          } else {
                            setBaseUnitCustomByRow(prev => ({ ...prev, [idx]: false }));
                            updateLineItem(idx, 'base_unit', val);
                          }
                        };
                        return (
                          <>
                            <select
                              id={`ocr-edit-base-unit-${idx}`}
                              className="input"
                              value={selectVal}
                              onChange={e => handleSelect(e.target.value)}
                            >
                              <option value="">{item.matched_sku_base_unit ? `(default: ${item.matched_sku_base_unit})` : '(none)'}</option>
                              {BASE_UNIT_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                              <option value={BASE_UNIT_NEW_SENTINEL}>+ Add new…</option>
                            </select>
                            {isCustom && (
                              <input
                                id={`ocr-edit-base-unit-${idx}-custom`}
                                type="text"
                                className="input"
                                placeholder="New base unit (e.g. cup, slice)"
                                value={item.base_unit || ''}
                                onChange={e => updateLineItem(idx, 'base_unit', e.target.value)}
                                style={{ marginTop: 'var(--sp1)' }}
                                autoFocus
                              />
                            )}
                          </>
                        );
                      })()}
                      <span className="hint">
                        {item.matched_sku_base_unit
                          ? `Defaults to "${item.matched_sku_base_unit}" from matched SKU`
                          : 'Override the SKU default (rare — usually leave blank)'}
                      </span>
                    </div>
                    <div className="field">
                      <label className="label" htmlFor={`ocr-edit-base-pack-${idx}`}>Base units per pack (override)</label>
                      <input
                        id={`ocr-edit-base-pack-${idx}`}
                        type="number"
                        step="0.001"
                        min="0.001"
                        className="input ocr-review__numeric"
                        placeholder={item.matched_sku_base_units_per_pack != null ? String(item.matched_sku_base_units_per_pack) : 'e.g. 12, 72'}
                        value={item.base_units_per_pack === '' || item.base_units_per_pack == null ? '' : item.base_units_per_pack}
                        onChange={e => updateLineItem(idx, 'base_units_per_pack', e.target.value)}
                        aria-describedby={`ocr-edit-base-pack-hint-${idx}`}
                      />
                      <span className="hint" id={`ocr-edit-base-pack-hint-${idx}`}>
                        {item.matched_sku_base_units_per_pack != null
                          ? `Defaults to ${item.matched_sku_base_units_per_pack} from matched SKU`
                          : 'Only needed when the line UOM is a pack (case, box, bag).'}
                      </span>
                    </div>
                  </div>
                  </details>
                  {addingCategoryForRow !== idx && (
                    <div className="field">
                      <label className="label">Category</label>
                      <div style={{ display: 'flex', gap: 'var(--sp2)' }}>
                        <select
                          className="input"
                          value={item.suggested_category?.category_id || item.suggested_category?._newKey || ''}
                          onChange={e => {
                            const catId = e.target.value;
                            const isNewCat = catId.toString().startsWith('new-');
                            if (isNewCat) {
                              const newIdx = parseInt(catId.replace('new-', ''));
                              const newCat = (data.new_categories || [])[newIdx];
                              updateLineItem(idx, 'suggested_category', { level1: newCat?.level1 || '', level2: newCat?.level2 || '', level3: newCat?.level3 || '', category_id: null, is_new: true, _newKey: catId });
                            } else {
                              updateLineItem(idx, 'suggested_category', { ...item.suggested_category, category_id: parseInt(catId) || null, is_new: false, _newKey: undefined });
                            }
                          }}
                          style={{ flex: 1 }}
                        >
                          <option value="">Select category...</option>
                          {categoryOptions.map(opt => (
                            <option key={opt.id} value={opt.id}>{opt.isNew ? `[NEW] ${opt.label}` : opt.label}</option>
                          ))}
                        </select>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setAddingCategoryForRow(idx); setNewCatLevel1(''); setNewCatLevel2(''); setNewCatLevel3(''); setNewCatL1Custom(false); setNewCatL2Custom(false); setNewCatL3Custom(false); }} title="Add new category">+</button>
                      </div>
                    </div>
                  )}
                  {addingCategoryForRow === idx && (() => {
                    const NEW_SENTINEL = '__add_new__';
                    const trimmedL1 = newCatLevel1.trim();
                    const trimmedL2 = newCatLevel2.trim();
                    const level1Options = Array.from(new Set(
                      categories.map(c => c.level1).filter(Boolean)
                    )).sort();
                    const level2Options = Array.from(new Set(
                      categories
                        .filter(c => !trimmedL1 || c.level1 === trimmedL1)
                        .map(c => c.level2)
                        .filter(Boolean)
                    )).sort();
                    const level3Options = Array.from(new Set(
                      categories
                        .filter(c =>
                          (!trimmedL1 || c.level1 === trimmedL1) &&
                          (!trimmedL2 || c.level2 === trimmedL2)
                        )
                        .map(c => c.level3)
                        .filter(Boolean)
                    )).sort();
                    // Select shows the picked value when it matches an existing option;
                    // shows the sentinel when the user is typing a brand-new value.
                    const l1SelectValue = newCatL1Custom ? NEW_SENTINEL : newCatLevel1;
                    const l2SelectValue = newCatL2Custom ? NEW_SENTINEL : newCatLevel2;
                    const l3SelectValue = newCatL3Custom ? NEW_SENTINEL : newCatLevel3;
                    const handleL1SelectChange = (val) => {
                      // Cascade reset: changing Level 1 clears Levels 2 and 3.
                      setNewCatLevel2('');
                      setNewCatLevel3('');
                      setNewCatL2Custom(false);
                      setNewCatL3Custom(false);
                      if (val === NEW_SENTINEL) {
                        setNewCatL1Custom(true);
                        setNewCatLevel1('');
                      } else {
                        setNewCatL1Custom(false);
                        setNewCatLevel1(val);
                      }
                    };
                    const handleL2SelectChange = (val) => {
                      // Cascade reset: changing Level 2 clears Level 3.
                      setNewCatLevel3('');
                      setNewCatL3Custom(false);
                      if (val === NEW_SENTINEL) {
                        setNewCatL2Custom(true);
                        setNewCatLevel2('');
                      } else {
                        setNewCatL2Custom(false);
                        setNewCatLevel2(val);
                      }
                    };
                    const handleL3SelectChange = (val) => {
                      if (val === NEW_SENTINEL) {
                        setNewCatL3Custom(true);
                        setNewCatLevel3('');
                      } else {
                        setNewCatL3Custom(false);
                        setNewCatLevel3(val);
                      }
                    };
                    return (
                      <div style={{ display: 'flex', gap: 'var(--sp2)', alignItems: 'flex-start', marginBottom: 'var(--sp3)' }}>
                        <div className="field" style={{ flex: 1 }}>
                          <label className="label" htmlFor="new-cat-level1-select">Level 1 *</label>
                          <select
                            id="new-cat-level1-select"
                            className="input"
                            value={l1SelectValue}
                            onChange={e => handleL1SelectChange(e.target.value)}
                          >
                            <option value="">Select…</option>
                            {level1Options.map(v => <option key={v} value={v}>{v}</option>)}
                            <option value={NEW_SENTINEL}>+ Add new…</option>
                          </select>
                          {newCatL1Custom && (
                            <input
                              id="new-cat-level1-input"
                              type="text"
                              className="input"
                              placeholder="New Level 1 name"
                              value={newCatLevel1}
                              onChange={e => setNewCatLevel1(e.target.value)}
                              style={{ marginTop: 'var(--sp1)' }}
                              autoFocus
                            />
                          )}
                        </div>
                        <div className="field" style={{ flex: 1 }}>
                          <label className="label" htmlFor="new-cat-level2-select">Level 2</label>
                          <select
                            id="new-cat-level2-select"
                            className="input"
                            value={l2SelectValue}
                            onChange={e => handleL2SelectChange(e.target.value)}
                          >
                            <option value="">(none)</option>
                            {level2Options.map(v => <option key={v} value={v}>{v}</option>)}
                            <option value={NEW_SENTINEL}>+ Add new…</option>
                          </select>
                          {newCatL2Custom && (
                            <input
                              id="new-cat-level2-input"
                              type="text"
                              className="input"
                              placeholder="New Level 2 name"
                              value={newCatLevel2}
                              onChange={e => setNewCatLevel2(e.target.value)}
                              style={{ marginTop: 'var(--sp1)' }}
                              autoFocus
                            />
                          )}
                        </div>
                        <div className="field" style={{ flex: 1 }}>
                          <label className="label" htmlFor="new-cat-level3-select">Level 3</label>
                          <select
                            id="new-cat-level3-select"
                            className="input"
                            value={l3SelectValue}
                            onChange={e => handleL3SelectChange(e.target.value)}
                          >
                            <option value="">(none)</option>
                            {level3Options.map(v => <option key={v} value={v}>{v}</option>)}
                            <option value={NEW_SENTINEL}>+ Add new…</option>
                          </select>
                          {newCatL3Custom && (
                            <input
                              id="new-cat-level3-input"
                              type="text"
                              className="input"
                              placeholder="New Level 3 name"
                              value={newCatLevel3}
                              onChange={e => setNewCatLevel3(e.target.value)}
                              style={{ marginTop: 'var(--sp1)' }}
                              autoFocus
                            />
                          )}
                        </div>
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => handleCreateInlineCategory(idx)}>Create</button>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAddingCategoryForRow(null)}>Cancel</button>
                      </div>
                    );
                  })()}
                  <div style={{ display: 'flex', gap: 'var(--sp2)', marginTop: 'var(--sp4)' }}>
                    <button type="button" className="btn btn-primary" onClick={() => { setEditingRowIdx(null); setAddingCategoryForRow(null); }}>Done</button>
                  </div>
                </div>
              );
            })()}
          </Modal>
        )}

        {/* Misc Charges */}
        {(data.misc_charges || []).length > 0 && (
        <div className="card ocr-review__card--mb">
          <div className="settings-form__table-header">
            <h3 className="settings-form__section-title" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
              Misc Charges ({(data.misc_charges || []).length})
            </h3>
            <button type="button" onClick={() => { setNewMiscForm({ description: '', amount: 0 }); setShowAddMiscModal(true); }} className="btn btn-secondary btn-sm">
              + Add Charge
            </button>
          </div>
          <div className="tbl-wrap">
            <table>
              <thead><tr><th scope="col">Description</th><th scope="col">Amount</th><th scope="col"></th></tr></thead>
              <tbody>
                {(data.misc_charges || []).map((charge, idx) => (
                  <tr key={idx}>
                    <td>{charge.description || '—'}</td>
                    <td><span className="td-amt">${(parseFloat(charge.amount) || 0).toFixed(2)}</span></td>
                    <td>
                      <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }} onClick={() => removeMiscCharge(idx)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* End of Items tab pane (mobile). */}
        </div>

        {/* Action Buttons — sticky footer. Outside the tab panes so it's
            always visible regardless of which tab is active on mobile. */}
        <div className="ocr-review__footer">
          {onBack && (
            <button type="button" className="btn btn-ghost" onClick={() => {
              if (isDirty) {
                setConfirmModal({
                  title: 'Unsaved Changes',
                  message: 'You have unsaved changes. Discard and go back?',
                  confirmLabel: 'Discard',
                  variant: 'danger',
                  onConfirm: () => { setConfirmModal(null); onBack(); },
                });
                return;
              }
              onBack();
            }}>
              Back
            </button>
          )}
          {onReExtract && (
            <button type="button" className="btn btn-secondary" disabled={reExtracting} onClick={() => {
              if (isDirty) {
                setConfirmModal({
                  title: 'Discard Edits',
                  message: 'Re-extracting will discard your manual edits. Continue?',
                  confirmLabel: 'Re-extract',
                  variant: 'primary',
                  onConfirm: () => { setConfirmModal(null); onReExtract(); },
                });
                return;
              }
              onReExtract();
            }}>
              {reExtracting ? 'Extracting...' : 'Re-extract'}
            </button>
          )}
          <button type="button" onClick={handleSaveInvoice} disabled={saving} className="btn btn-primary">
            {saving ? 'Saving...' : 'Save Invoice'}
          </button>
        </div>
        </>)}
      </div>

      {/* Zoom Lightbox Modal */}
      {zoomOpen && previewImage && (
        <div
          ref={zoomModalRef}
          data-testid="zoom-modal"
          className="ocr-review__zoom-overlay"
          style={{
            cursor: zoomLevel >= 4
              ? (isDragging ? 'grabbing' : 'zoom-out')
              : zoomLevel > 1
                ? (isDragging ? 'grabbing' : 'grab')
                : 'zoom-in',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closeZoom(); }}
          onMouseDown={handlePanStart}
          onMouseMove={handlePanMove}
          onMouseUp={handlePanEnd}
          onMouseLeave={handlePanEnd}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onDoubleClick={handleDoubleClick}
          role="dialog" aria-modal="true" aria-label="Invoice preview zoom"
        >
          <div className="ocr-review__zoom-controls">
            <button type="button"
              onClick={() => setZoomLevel(prev => Math.min(prev + 0.5, 4))}
              className="ocr-review__zoom-btn"
              aria-label="Zoom in">+</button>
            <span className="ocr-review__zoom-level">
              {Math.round(zoomLevel * 100)}%
            </span>
            <button type="button"
              onClick={() => {
                setZoomLevel(prev => {
                  const newZoom = Math.max(prev - 0.5, 1);
                  if (newZoom <= 1) setPanPosition({ x: 0, y: 0 });
                  return newZoom;
                });
              }}
              className="ocr-review__zoom-btn"
              aria-label="Zoom out">-</button>
            <button type="button" onClick={closeZoom}
              className="ocr-review__zoom-btn--close"
              aria-label="Close zoom">X</button>
          </div>
          <div onClick={e => e.stopPropagation()} onDoubleClick={handleDoubleClick}>
            <img src={`data:image/jpeg;base64,${previewImage}`} alt="Invoice PDF (zoomed)"
              className="ocr-review__zoom-img"
              style={{
                transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                transition: isDragging ? 'none' : 'transform 0.2s ease',
              }}
              draggable={false} />
          </div>
        </div>
      )}
      {confirmModal && (
        <ConfirmModal
          isOpen={true}
          title={confirmModal.title}
          message={confirmModal.message}
          description={confirmModal.description}
          confirmLabel={confirmModal.confirmLabel}
          variant={confirmModal.variant}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}
      {showExistingInvoice && existingInvoiceId && (
        <InvoiceDetailModal
          invoiceId={existingInvoiceId}
          isOpen={true}
          onClose={() => setShowExistingInvoice(false)}
        />
      )}
      <QuickAddVendorModal
        isOpen={showVendorModal}
        onClose={() => setShowVendorModal(false)}
        onVendorCreated={async (newVendor) => {
          try {
            const res = await api.getVendors();
            setVendors(res.data.filter(v => v.status === 'active'));
          } catch { /* ignore */ }
          setSelectedVendorId(String(newVendor.id));
        }}
      />
    </div>
  );
}

ClaudeOCRReview.propTypes = {
  /** Data from COGSLOG extraction */
  extractedData: PropTypes.object,
  /** Server path to the PDF */
  filepath: PropTypes.string,
  /** Base64 preview image */
  previewImage: PropTypes.string,
  /** Detected vendor ID from upload step */
  vendorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Detected vendor name from upload step */
  vendorName: PropTypes.string,
  /** Currently selected business ID */
  activeBusiness: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Callback to retry extraction */
  onReExtract: PropTypes.func,
  /** Whether re-extraction is in progress */
  reExtracting: PropTypes.bool,
  /** Callback to go back to upload */
  onBack: PropTypes.func,
  /** Queue item ID (when rendered from batch queue) */
  queueItemId: PropTypes.number,
  /** Queue item filename (for PDF append tracking) */
  queueFilename: PropTypes.string,
  /** Callback to delete item from queue (batch mode) */
  onDeleteFromQueue: PropTypes.func,
  /** Callback to advance to next item (batch mode) */
  onReviewNext: PropTypes.func,
  /** Callback to return to queue (batch mode) */
  onCloseReview: PropTypes.func,
  /** Callback when dirty state changes */
  onDirtyChange: PropTypes.func,
};

export default ClaudeOCRReview;
